import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getAuthToken } from '../api/api';
import CompraSemana from '../components/compras/CompraSemana';
import { ddmm, textoDia } from '../components/compras/listaCompraUtils';
import { ShoppingCart, Calendar, AlertCircle, CheckCircle, AlertTriangle, TrendingUp, Package, Users, ChevronDown, ChevronRight, Truck, Edit2, Eye, X, Trash2, DollarSign, ArrowUpRight, ArrowDownRight, Clock, Check, XCircle, FileText, Plus, Minus, Download } from 'lucide-react';

// Componente para Tab "Recibir Mercancía"
const TabRecibirMercancia = () => {
  const [metodo, setMetodo] = useState('orden'); // 'orden' o 'manual'
  const [ordenesDisponibles, setOrdenesDisponibles] = useState([]);
  const [ordenSeleccionada, setOrdenSeleccionada] = useState(null);
  const [ingredientes, setIngredientes] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    albaran_proveedor: '',
    items: [] // Each item: { ingrediente_id, nombre, cantidad_pedida, unidad, precio_estimado, precio_unitario, incluyeIva, tipo_iva, lotes: [{cantidad_recibida, lote_proveedor, fecha_caducidad}] }
  });
  const [formManual, setFormManual] = useState({
    ingrediente_id: '',
    proveedor_id: '',
    lote_proveedor: '',
    fecha_caducidad: '',
    cantidad_inicial: '',
    unidad: '',
    precio_unitario: '',
    albaran_proveedor: '',
    notas: ''
  });

  useEffect(() => {
    fetchOrdenesDisponibles();
    fetchIngredientes();
    fetchProveedores();
  }, []);

  const fetchOrdenesDisponibles = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ordenes-compra`,
        { headers: { 'Authorization': `Bearer ${getAuthToken()}` } }
      );
      const data = await response.json();
      const ordenesFiltradas = (data || []).filter(o =>
        o.estado === 'solicitado' || o.estado === 'recibido_parcial'
      );
      setOrdenesDisponibles(ordenesFiltradas);
    } catch (error) {
      console.error('Error fetching ordenes:', error);
    }
  };

  const fetchIngredientes = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ingredientes`,
        { headers: { 'Authorization': `Bearer ${getAuthToken()}` } }
      );
      const data = await response.json();
      setIngredientes(data || []);
    } catch (error) {
      console.error('Error fetching ingredientes:', error);
    }
  };

  const fetchProveedores = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/proveedores`,
        { headers: { 'Authorization': `Bearer ${getAuthToken()}` } }
      );
      const data = await response.json();
      setProveedores(data || []);
    } catch (error) {
      console.error('Error fetching proveedores:', error);
    }
  };

  // Helper: round to 2 decimals
  const round2 = (v) => Math.round((v || 0) * 100) / 100;

  // Helper: calculate price sin IVA from con IVA
  const precioSinIva = (precioConIva, tipoIva) => {
    if (!tipoIva || tipoIva <= 0) return precioConIva;
    return round2(precioConIva / (1 + tipoIva / 100));
  };

  // Get effective price (sin IVA) for an item
  const getPrecioEfectivo = (item) => {
    if (item.incluyeIva && item.tipo_iva > 0) {
      return precioSinIva(item.precio_unitario, item.tipo_iva);
    }
    return item.precio_unitario;
  };

  const handleOrdenChange = (e) => {
    const ordenId = e.target.value;
    const orden = ordenesDisponibles.find(o => o.id === ordenId);
    setOrdenSeleccionada(orden);

    if (orden) {
      const items = orden.items.map(item => {
        // Find ingredient to get tipo_iva
        const ing = ingredientes.find(i => i.id === item.ingrediente_id);
        const tipoIva = ing?.tipo_iva || 10;
        const precioBase = round2(item.precio_estimado || item.precio_unitario || 0);
        return {
          ingrediente_id: item.ingrediente_id,
          nombre: item.nombre,
          cantidad_pedida: item.cantidad_pedida,
          unidad: item.unidad,
          precio_estimado: precioBase,
          precio_unitario: precioBase,
          incluyeIva: false,
          tipo_iva: tipoIva,
          lotes: [{ cantidad_recibida: 0, lote_proveedor: '', fecha_caducidad: '' }]
        };
      });
      setFormData({ ...formData, items });
    }
  };

  const handleItemFieldChange = (itemIdx, field, value) => {
    const newItems = [...formData.items];
    newItems[itemIdx] = { ...newItems[itemIdx], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const handleLoteChange = (itemIdx, loteIdx, field, value) => {
    const newItems = [...formData.items];
    const newLotes = [...newItems[itemIdx].lotes];
    newLotes[loteIdx] = { ...newLotes[loteIdx], [field]: value };
    newItems[itemIdx] = { ...newItems[itemIdx], lotes: newLotes };
    setFormData({ ...formData, items: newItems });
  };

  const addLote = (itemIdx) => {
    const newItems = [...formData.items];
    newItems[itemIdx] = {
      ...newItems[itemIdx],
      lotes: [...newItems[itemIdx].lotes, { cantidad_recibida: 0, lote_proveedor: '', fecha_caducidad: '' }]
    };
    setFormData({ ...formData, items: newItems });
  };

  const removeLote = (itemIdx, loteIdx) => {
    const newItems = [...formData.items];
    if (newItems[itemIdx].lotes.length <= 1) return;
    const newLotes = newItems[itemIdx].lotes.filter((_, i) => i !== loteIdx);
    newItems[itemIdx] = { ...newItems[itemIdx], lotes: newLotes };
    setFormData({ ...formData, items: newItems });
  };

  // Total received across all lots for an item
  const totalRecibido = (item) =>
    item.lotes.reduce((sum, l) => sum + (parseFloat(l.cantidad_recibida) || 0), 0);

  const handleRecibirOrden = async () => {
    if (!ordenSeleccionada) { alert('Selecciona una orden primero'); return; }

    // Flatten: expand items with multiple lots into individual backend items
    const itemsARecibir = [];
    for (const item of formData.items) {
      const precioFinal = round2(getPrecioEfectivo(item));
      for (const lote of item.lotes) {
        const cant = parseFloat(lote.cantidad_recibida) || 0;
        if (cant <= 0) continue;
        if (!lote.lote_proveedor || !lote.fecha_caducidad) {
          alert(`El ingrediente "${item.nombre}" tiene lotes sin número de lote o fecha de caducidad`);
          return;
        }
        itemsARecibir.push({
          ingrediente_id: item.ingrediente_id,
          cantidad_recibida: cant,
          lote_proveedor: lote.lote_proveedor,
          fecha_caducidad: lote.fecha_caducidad,
          precio_unitario: precioFinal
        });
      }
    }

    if (itemsARecibir.length === 0) {
      alert('Debes ingresar al menos una cantidad recibida');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ordenes-compra/${ordenSeleccionada.id}/recibir`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            albaran_proveedor: formData.albaran_proveedor,
            items: itemsARecibir
          })
        }
      );

      if (response.ok) {
        const result = await response.json();
        let msg = `Mercancía recibida: ${result.lotes_creados} lote(s) creado(s)`;
        if (result.alertas_precio && result.alertas_precio.length > 0) {
          msg += `\n\nAlertas de precio detectadas:\n${result.alertas_precio.map(a =>
            `  - ${a.ingrediente}: ${a.precio_anterior}€ → ${a.precio_nuevo}€ (${a.diferencia})`
          ).join('\n')}`;
        }
        alert(msg);
        setOrdenSeleccionada(null);
        setFormData({ albaran_proveedor: '', items: [] });
        fetchOrdenesDisponibles();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al recibir mercancía');
    } finally {
      setLoading(false);
    }
  };

  const handleRecibirManual = async (e) => {
    e.preventDefault();
    if (!formManual.ingrediente_id || !formManual.proveedor_id || !formManual.cantidad_inicial) {
      alert('Completa los campos obligatorios');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/lotes-ingredientes`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...formManual,
            cantidad_inicial: parseFloat(formManual.cantidad_inicial),
            precio_unitario: formManual.precio_unitario ? round2(parseFloat(formManual.precio_unitario)) : null
          })
        }
      );
      if (response.ok) {
        const result = await response.json();
        alert(`${result.message}\n${result.cantidad} ${result.unidad} de ${result.ingrediente}`);
        setFormManual({ ingrediente_id: '', proveedor_id: '', lote_proveedor: '', fecha_caducidad: '', cantidad_inicial: '', unidad: '', precio_unitario: '', albaran_proveedor: '', notas: '' });
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al crear lote');
    } finally {
      setLoading(false);
    }
  };

  const handleIngredienteChange = (e) => {
    const ingredienteId = e.target.value;
    const ingrediente = ingredientes.find(i => i.id === ingredienteId);
    setFormManual({
      ...formManual,
      ingrediente_id: ingredienteId,
      unidad: ingrediente?.unidad || '',
      proveedor_id: ingrediente?.proveedor_id || ''
    });
  };

  // Format price input: limit to 2 decimal places
  const handlePrecioInput = (itemIdx, rawValue) => {
    if (rawValue === '') {
      handleItemFieldChange(itemIdx, 'precio_unitario', '');
      return;
    }
    // Allow typing but limit to 2 decimal places
    const parts = String(rawValue).split('.');
    if (parts.length > 1 && parts[1].length > 2) return; // block >2 decimals
    handleItemFieldChange(itemIdx, 'precio_unitario', rawValue === '' ? '' : parseFloat(rawValue));
  };

  return (
    <div className="space-y-6">
      {/* Selector de Método */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Método de Recepción</h2>
        <div className="flex gap-4">
          <button
            onClick={() => setMetodo('orden')}
            className={`flex-1 p-4 rounded-lg border-2 transition-all ${
              metodo === 'orden'
                ? 'border-duetto-primary bg-duetto-primary/5'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                metodo === 'orden' ? 'bg-duetto-primary text-white' : 'bg-gray-100 text-gray-600'
              }`}>
                <ShoppingCart className="w-6 h-6" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-900">Desde Orden de Compra</h3>
                <p className="text-sm text-gray-600">Recibir mercancía de una orden pendiente</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => setMetodo('manual')}
            className={`flex-1 p-4 rounded-lg border-2 transition-all ${
              metodo === 'manual'
                ? 'border-duetto-primary bg-duetto-primary/5'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                metodo === 'manual' ? 'bg-duetto-primary text-white' : 'bg-gray-100 text-gray-600'
              }`}>
                <Package className="w-6 h-6" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-900">Entrada Manual</h3>
                <p className="text-sm text-gray-600">Registrar mercancía sin orden previa</p>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* MÉTODO 1: Desde Orden de Compra */}
      {metodo === 'orden' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Recibir desde Orden de Compra</h2>

          <div className="space-y-4">
            {/* Selector de Orden */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Seleccionar Orden Pendiente *
              </label>
              <select
                value={ordenSeleccionada?.id || ''}
                onChange={handleOrdenChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                data-testid="select-orden-recibir"
              >
                <option value="">-- Selecciona una orden --</option>
                {[...ordenesDisponibles].sort((a, b) => (a.proveedor_nombre || '').localeCompare(b.proveedor_nombre || '')).map(orden => (
                  <option key={orden.id} value={orden.id}>
                    {orden.proveedor_nombre} - {new Date(orden.fecha_pedido).toLocaleDateString('es-ES')} ({orden.items?.length || 0} items)
                  </option>
                ))}
              </select>
              {ordenesDisponibles.length === 0 && (
                <p className="text-sm text-gray-500 mt-2">No hay órdenes pendientes</p>
              )}
            </div>

            {/* Albarán */}
            {ordenSeleccionada && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Número de Albarán
                  </label>
                  <input
                    type="text"
                    value={formData.albaran_proveedor}
                    onChange={(e) => setFormData({ ...formData, albaran_proveedor: e.target.value })}
                    placeholder="Ej: ALB-2025-001"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                    data-testid="input-albaran"
                  />
                </div>

                {/* Tabla de Items con multi-lote */}
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="tabla-recepcion-items">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ingrediente</th>
                        <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Pedido</th>
                        <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Recibir *</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lote *</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Caducidad *</th>
                        <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Precio Esp.</th>
                        <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Precio Real (sin IVA)</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {formData.items.map((item, itemIdx) => {
                        const precioEsperado = item.precio_estimado || 0;
                        const precioEfectivo = getPrecioEfectivo(item);
                        const diferencia = precioEfectivo - precioEsperado;
                        const porcentajeCambio = precioEsperado > 0 ? ((diferencia / precioEsperado) * 100) : 0;
                        const hayDiferencia = Math.abs(porcentajeCambio) > 5;
                        const totalRec = totalRecibido(item);

                        return (
                          <React.Fragment key={itemIdx}>
                            {item.lotes.map((lote, loteIdx) => {
                              const isFirst = loteIdx === 0;
                              return (
                                <tr key={`${itemIdx}-${loteIdx}`} className={hayDiferencia && precioEfectivo > 0 ? 'bg-yellow-50' : ''} data-testid={`lote-${itemIdx}-${loteIdx}`}>
                                  {/* Ingrediente */}
                                  <td className="px-3 py-2">
                                    {isFirst ? (
                                      <>
                                        <div className="font-medium text-gray-900">{item.nombre}</div>
                                        <div className="text-xs text-gray-400">IVA: {item.tipo_iva}%</div>
                                      </>
                                    ) : null}
                                  </td>
                                  {/* Pedido */}
                                  <td className="px-3 py-2 text-center text-gray-600">
                                    {isFirst ? (
                                      <>
                                        <div>{item.cantidad_pedida} {item.unidad}</div>
                                        {item.lotes.length > 1 && (
                                          <div className={`text-xs font-medium ${totalRec > item.cantidad_pedida ? 'text-red-600' : totalRec > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                                            Rec: {round2(totalRec)}
                                          </div>
                                        )}
                                      </>
                                    ) : null}
                                  </td>
                                  {/* Recibir */}
                                  <td className="px-3 py-2 text-center">
                                    <input
                                      type="number" step="0.01" min="0"
                                      value={lote.cantidad_recibida}
                                      onChange={(e) => handleLoteChange(itemIdx, loteIdx, 'cantidad_recibida', e.target.value === '' ? '' : parseFloat(e.target.value))}
                                      onBlur={(e) => { if (e.target.value === '') handleLoteChange(itemIdx, loteIdx, 'cantidad_recibida', 0); }}
                                      className="w-24 px-2 py-1 border border-gray-300 rounded text-center text-sm focus:ring-2 focus:ring-duetto-primary"
                                      data-testid={`lote-cantidad-${itemIdx}-${loteIdx}`}
                                    />
                                  </td>
                                  {/* Lote */}
                                  <td className="px-3 py-2">
                                    <input
                                      type="text"
                                      value={lote.lote_proveedor}
                                      onChange={(e) => handleLoteChange(itemIdx, loteIdx, 'lote_proveedor', e.target.value)}
                                      placeholder="LOTE-123"
                                      className="w-32 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-duetto-primary"
                                      data-testid={`lote-numero-${itemIdx}-${loteIdx}`}
                                    />
                                  </td>
                                  {/* Caducidad */}
                                  <td className="px-3 py-2">
                                    <input
                                      type="date"
                                      value={lote.fecha_caducidad}
                                      onChange={(e) => handleLoteChange(itemIdx, loteIdx, 'fecha_caducidad', e.target.value)}
                                      className="w-36 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-duetto-primary"
                                      data-testid={`lote-caducidad-${itemIdx}-${loteIdx}`}
                                    />
                                  </td>
                                  {/* Precio Esperado */}
                                  <td className="px-3 py-2 text-right">
                                    {isFirst ? (
                                      <div className="text-sm font-medium text-gray-700">€{precioEsperado.toFixed(2)}</div>
                                    ) : null}
                                  </td>
                                  {/* Precio Real */}
                                  <td className="px-3 py-2">
                                    {isFirst ? (
                                      <div className="space-y-1">
                                        <input
                                          type="number" step="0.01" min="0"
                                          value={item.precio_unitario}
                                          onChange={(e) => handlePrecioInput(itemIdx, e.target.value)}
                                          onBlur={(e) => {
                                            if (e.target.value === '') handleItemFieldChange(itemIdx, 'precio_unitario', 0);
                                            else handleItemFieldChange(itemIdx, 'precio_unitario', round2(parseFloat(e.target.value)));
                                          }}
                                          className={`w-28 px-2 py-1 border rounded text-right text-sm focus:ring-2 focus:ring-duetto-primary ${
                                            hayDiferencia ? 'border-yellow-500 bg-yellow-50' : 'border-gray-300'
                                          }`}
                                          data-testid={`precio-real-${itemIdx}`}
                                        />
                                        <div className="flex items-center gap-1.5">
                                          <button
                                            onClick={() => handleItemFieldChange(itemIdx, 'incluyeIva', !item.incluyeIva)}
                                            className={`relative inline-flex h-4 w-8 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                                              item.incluyeIva ? 'bg-duetto-primary' : 'bg-gray-200'
                                            }`}
                                            data-testid={`toggle-iva-${itemIdx}`}
                                          >
                                            <span className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${
                                              item.incluyeIva ? 'translate-x-4' : 'translate-x-0'
                                            }`} />
                                          </button>
                                          <span className="text-xs text-gray-500">{item.incluyeIva ? 'IVA incl.' : 'Sin IVA'}</span>
                                        </div>
                                        {item.incluyeIva && item.precio_unitario > 0 && (
                                          <div className="text-xs bg-blue-50 text-blue-700 rounded px-1.5 py-0.5 whitespace-nowrap">
                                            → <strong>€{precioSinIva(item.precio_unitario, item.tipo_iva).toFixed(2)}</strong> sin IVA ({item.tipo_iva}%)
                                          </div>
                                        )}
                                        {hayDiferencia && precioEfectivo > 0 && (
                                          <div className={`text-xs font-medium ${diferencia > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                            {diferencia > 0 ? '↑' : '↓'} {Math.abs(porcentajeCambio).toFixed(1)}%
                                          </div>
                                        )}
                                      </div>
                                    ) : null}
                                  </td>
                                  {/* Remove extra lot */}
                                  <td className="px-1 py-2">
                                    {!isFirst && (
                                      <button
                                        onClick={() => removeLote(itemIdx, loteIdx)}
                                        className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                        title="Eliminar lote"
                                        data-testid={`remove-lote-${itemIdx}-${loteIdx}`}
                                      >
                                        <Minus className="w-4 h-4" />
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                            {/* + Añadir lote */}
                            <tr className="border-b border-gray-200">
                              <td colSpan={2}></td>
                              <td colSpan={3} className="px-3 py-1">
                                <button
                                  onClick={() => addLote(itemIdx)}
                                  className="flex items-center gap-1 text-xs text-duetto-primary hover:text-duetto-secondary font-medium px-1 py-0.5 rounded hover:bg-duetto-primary/5 transition-colors"
                                  data-testid={`add-lote-${itemIdx}`}
                                >
                                  <Plus className="w-3.5 h-3.5" />
                                  Añadir lote
                                </button>
                              </td>
                              <td colSpan={3}></td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    onClick={() => {
                      setOrdenSeleccionada(null);
                      setFormData({ albaran_proveedor: '', items: [] });
                    }}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    data-testid="btn-cancelar-recepcion"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleRecibirOrden}
                    disabled={loading}
                    className="px-6 py-2 bg-duetto-primary text-white rounded-lg hover:bg-duetto-secondary transition-colors disabled:opacity-50"
                    data-testid="btn-recibir-mercancia"
                  >
                    {loading ? 'Procesando...' : 'Recibir Mercancía'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* MÉTODO 2: Entrada Manual */}
      {metodo === 'manual' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Entrada Manual de Mercancía</h2>

          <form onSubmit={handleRecibirManual} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Ingrediente */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ingrediente *
                </label>
                <select
                  value={formManual.ingrediente_id}
                  onChange={handleIngredienteChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                >
                  <option value="">-- Selecciona ingrediente --</option>
                  {[...ingredientes].sort((a, b) => a.nombre.localeCompare(b.nombre)).map(ing => (
                    <option key={ing.id} value={ing.id}>
                      {ing.nombre} ({ing.unidad})
                    </option>
                  ))}
                </select>
              </div>

              {/* Proveedor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Proveedor *
                </label>
                <select
                  value={formManual.proveedor_id}
                  onChange={(e) => setFormManual({ ...formManual, proveedor_id: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                >
                  <option value="">-- Selecciona proveedor --</option>
                  {[...proveedores].sort((a, b) => a.nombre.localeCompare(b.nombre)).map(prov => (
                    <option key={prov.id} value={prov.id}>
                      {prov.nombre}
                    </option>
                  ))}
                </select>
              </div>

              {/* Lote Proveedor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Número de Lote *
                </label>
                <input
                  type="text"
                  value={formManual.lote_proveedor}
                  onChange={(e) => setFormManual({ ...formManual, lote_proveedor: e.target.value })}
                  required
                  placeholder="LOTE-2025-001"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                />
              </div>

              {/* Fecha Caducidad */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de Caducidad *
                </label>
                <input
                  type="date"
                  value={formManual.fecha_caducidad}
                  onChange={(e) => setFormManual({ ...formManual, fecha_caducidad: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                />
              </div>

              {/* Cantidad */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cantidad Recibida *
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formManual.cantidad_inicial}
                    onChange={(e) => setFormManual({ ...formManual, cantidad_inicial: e.target.value })}
                    required
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                  />
                  <input
                    type="text"
                    value={formManual.unidad}
                    readOnly
                    className="w-20 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                  />
                </div>
              </div>

              {/* Precio Unitario */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Precio Unitario sin IVA (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formManual.precio_unitario}
                  onChange={(e) => {
                    const parts = String(e.target.value).split('.');
                    if (parts.length > 1 && parts[1].length > 2) return;
                    setFormManual({ ...formManual, precio_unitario: e.target.value });
                  }}
                  onBlur={(e) => {
                    if (e.target.value !== '') {
                      setFormManual({ ...formManual, precio_unitario: round2(parseFloat(e.target.value)).toString() });
                    }
                  }}
                  placeholder="0.00"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                />
              </div>

              {/* Albarán */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Número de Albarán
                </label>
                <input
                  type="text"
                  value={formManual.albaran_proveedor}
                  onChange={(e) => setFormManual({ ...formManual, albaran_proveedor: e.target.value })}
                  placeholder="ALB-2025-001"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                />
              </div>

              {/* Notas */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notas / Observaciones
                </label>
                <textarea
                  value={formManual.notas}
                  onChange={(e) => setFormManual({ ...formManual, notas: e.target.value })}
                  rows={3}
                  placeholder="Información adicional sobre esta recepción..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => setFormManual({ ingrediente_id: '', proveedor_id: '', lote_proveedor: '', fecha_caducidad: '', cantidad_inicial: '', unidad: '', precio_unitario: '', albaran_proveedor: '', notas: '' })}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Limpiar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-duetto-primary text-white rounded-lg hover:bg-duetto-secondary transition-colors disabled:opacity-50"
              >
                {loading ? 'Creando Lote...' : 'Crear Lote'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

// Componente para Tab "Hacer Pedido"
const TabHacerPedido = ({ abrirModalEnvioEmail, ordenDestacada = null }) => {
  // Con ?orden=<id> (enlace desde la lista de compra) se abre en Órdenes de Compra
  const [subTab, setSubTab] = useState(ordenDestacada ? 'ordenes' : 'stock'); // 'stock', 'eventos' o 'ordenes'

  useEffect(() => {
    if (ordenDestacada) setSubTab('ordenes');
  }, [ordenDestacada]);

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2">
        <div className="flex gap-2">
          <button
            onClick={() => setSubTab('stock')}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              subTab === 'stock' ? 'bg-duetto-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Stock e Ingredientes
          </button>
          <button
            onClick={() => setSubTab('eventos')}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              subTab === 'eventos' ? 'bg-duetto-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Por Evento
          </button>
          <button
            onClick={() => setSubTab('ordenes')}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              subTab === 'ordenes' ? 'bg-duetto-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Órdenes de Compra
          </button>
        </div>
      </div>

      {subTab === 'stock' && <SubTabStock />}
      {subTab === 'eventos' && <SubTabListasEvento abrirModalEnvioEmail={abrirModalEnvioEmail} />}
      {subTab === 'ordenes' && <SubTabOrdenes abrirModalEnvioEmail={abrirModalEnvioEmail} ordenDestacada={ordenDestacada} />}
    </div>
  );
};

// Sub-Tab de Listas de Compra por Evento
const SubTabListasEvento = ({ abrirModalEnvioEmail }) => {
  const [listas, setListas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listaExpandida, setListaExpandida] = useState(null);
  const [proveedorExpandido, setProveedorExpandido] = useState({});
  const [creandoOrden, setCreandoOrden] = useState(false);

  useEffect(() => {
    fetchListas();
  }, []);

  const fetchListas = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/listas-compra`,
        {
          headers: { 'Authorization': `Bearer ${getAuthToken()}` }
        }
      );
      const data = await response.json();
      setListas(data.listas || []);
    } catch (error) {
      console.error('Error fetching listas:', error);
    } finally {
      setLoading(false);
    }
  };

  const crearOrdenDesdeProveedor = async (listaId, proveedorId) => {
    setCreandoOrden(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/listas-compra/${listaId}/crear-orden/${proveedorId}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${getAuthToken()}` }
        }
      );
      const data = await response.json();
      if (response.ok) {
        alert(`Orden de compra creada: ${data.message}`);
        fetchListas(); // Refrescar
      } else {
        alert(`Error: ${data.detail || data.message}`);
      }
    } catch (error) {
      console.error('Error creando orden:', error);
      alert('Error al crear orden de compra');
    } finally {
      setCreandoOrden(false);
    }
  };

  const copiarTextoPedido = async (listaId, proveedorId, proveedorNombre) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/listas-compra/${listaId}/texto-pedido/${proveedorId}`,
        {
          headers: { 'Authorization': `Bearer ${getAuthToken()}` }
        }
      );
      const data = await response.json();
      if (data.texto) {
        await navigator.clipboard.writeText(data.texto);
        alert(`Texto del pedido copiado al portapapeles para ${proveedorNombre}`);
      }
    } catch (error) {
      console.error('Error copiando texto:', error);
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'completada': return 'bg-green-100 text-green-800';
      case 'en_proceso': return 'bg-blue-100 text-blue-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-duetto-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Listas de Compra por Evento</h3>
            <p className="text-sm text-gray-600">Se generan automáticamente al confirmar un presupuesto</p>
          </div>
          <button
            onClick={fetchListas}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Actualizar
          </button>
        </div>
      </div>

      {/* Lista de eventos */}
      {listas.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <ShoppingCart className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No hay listas de compra</h3>
          <p className="text-gray-500 mt-2">Las listas se generan automáticamente cuando confirmas un presupuesto</p>
        </div>
      ) : (
        <div className="space-y-4">
          {listas.map(lista => (
            <div key={lista.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Cabecera del evento */}
              <div 
                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setListaExpandida(listaExpandida === lista.id ? null : lista.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {listaExpandida === lista.id ? 
                      <ChevronDown className="w-5 h-5 text-gray-400" /> : 
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    }
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">{lista.cliente_nombre}</span>
                        <span className="text-sm text-gray-500">#{lista.presupuesto_numero}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getEstadoColor(lista.estado)}`}>
                          {lista.estado}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {lista.fechas_texto
                            ? lista.fechas_texto
                            : new Date(lista.fecha_evento).toLocaleDateString('es-ES', {
                              weekday: 'short', day: 'numeric', month: 'short'
                            })}
                        </span>
                        <span>{lista.total_items} productos</span>
                        <span>{lista.total_proveedores} proveedores</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-900">€{(lista.coste_estimado_total || 0).toFixed(2)}</p>
                    <p className="text-xs text-gray-500">estimado</p>
                  </div>
                </div>
              </div>

              {/* Contenido expandido - Proveedores */}
              {listaExpandida === lista.id && (
                <div className="border-t border-gray-200 bg-gray-50 p-4 space-y-3">
                  {lista.items_por_proveedor?.map(proveedor => (
                    <div key={proveedor.proveedor_id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      {/* Cabecera del proveedor */}
                      <div 
                        className="p-3 cursor-pointer hover:bg-gray-50 flex items-center justify-between"
                        onClick={(e) => {
                          e.stopPropagation();
                          setProveedorExpandido(prev => ({
                            ...prev,
                            [proveedor.proveedor_id]: !prev[proveedor.proveedor_id]
                          }));
                        }}
                      >
                        <div className="flex items-center gap-3">
                          {proveedorExpandido[proveedor.proveedor_id] ? 
                            <ChevronDown className="w-4 h-4 text-gray-400" /> : 
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          }
                          <Truck className="w-5 h-5 text-duetto-primary" />
                          <div>
                            <p className="font-medium text-gray-900">{proveedor.proveedor_nombre}</p>
                            <p className="text-xs text-gray-500">{proveedor.total_items} productos • €{(proveedor.coste_estimado_proveedor || 0).toFixed(2)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                          <button
                            onClick={() => copiarTextoPedido(lista.id, proveedor.proveedor_id, proveedor.proveedor_nombre)}
                            className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                            title="Copiar texto para WhatsApp"
                          >
                            📋 Copiar
                          </button>
                          {proveedor.proveedor_id !== 'sin_proveedor' && (
                            <button
                              onClick={() => crearOrdenDesdeProveedor(lista.id, proveedor.proveedor_id)}
                              disabled={creandoOrden}
                              className="px-3 py-1.5 text-sm bg-duetto-primary text-white rounded-lg hover:bg-duetto-secondary transition-colors disabled:opacity-50"
                            >
                              {creandoOrden ? '...' : '+ Crear Orden'}
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Items del proveedor */}
                      {proveedorExpandido[proveedor.proveedor_id] && (
                        <div className="border-t border-gray-200">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left font-medium text-gray-600">Producto</th>
                                <th className="px-3 py-2 text-right font-medium text-gray-600">Necesario</th>
                                <th className="px-3 py-2 text-right font-medium text-gray-600">En Stock</th>
                                <th className="px-3 py-2 text-right font-medium text-gray-600">A Pedir</th>
                                <th className="px-3 py-2 text-right font-medium text-gray-600">€ Est.</th>
                                <th className="px-3 py-2 text-center font-medium text-gray-600">Estado</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {proveedor.items?.map(item => (
                                <tr key={item.id} className="hover:bg-gray-50">
                                  <td className="px-3 py-2 text-gray-900">{item.nombre}</td>
                                  <td className="px-3 py-2 text-right text-gray-600">
                                    {item.cantidad_necesaria?.toFixed(2)} {item.unidad}
                                  </td>
                                  <td className="px-3 py-2 text-right text-gray-600">
                                    {item.stock_disponible?.toFixed(2)} {item.unidad}
                                  </td>
                                  <td className="px-3 py-2 text-right font-medium text-gray-900">
                                    {item.cantidad_a_pedir?.toFixed(2)} {item.unidad}
                                  </td>
                                  <td className="px-3 py-2 text-right text-gray-600">
                                    €{(item.coste_estimado || 0).toFixed(2)}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    {item.estado === 'pedido' ? (
                                      <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800 rounded-full">Pedido</span>
                                    ) : (
                                      <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full">Pendiente</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Sub-Tab de Stock e Ingredientes
const SubTabStock = () => {
  const [ingredientes, setIngredientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [diasAdelante, setDiasAdelante] = useState(7);
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroProveedor, setFiltroProveedor] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [cantidades, setCantidades] = useState({});
  const [proveedores, setProveedores] = useState([]);
  const [alergenos, setAlergenos] = useState([]);
  const [paginaActual, setPaginaActual] = useState(1);
  const [ingredienteSeleccionado, setIngredienteSeleccionado] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    proveedor_id: '',
    precio_unitario: 0,
    unidad: 'kg',
    precio_paquete: 0,
    unidades_por_paquete: 1,
    stock_minimo: 0,
    tipo_iva: 10,
    alergenos_ids: [],
    carne: false,
    origen_animal: false,
    pasteurizado: false,
    activo: true
  });
  const ingredientesPorPagina = 20;

  useEffect(() => {
    fetchStock();
    fetchProveedores();
    fetchAlergenos();
  }, [diasAdelante]);

  const fetchStock = async () => {
    setLoading(true);
    // Limpiar selección cuando cambian los filtros
    setSeleccionados(new Set());
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stock-proyectado?dias_adelante=${diasAdelante}`,
        {
          headers: { 'Authorization': `Bearer ${getAuthToken()}` }
        }
      );
      const data = await response.json();
      const ings = data.ingredientes || [];
      setIngredientes(ings);
      
      // Inicializar cantidades sugeridas convertidas a envases completos
      const cant = {};
      ings.forEach(ing => {
        const unidadesPorEnvase = ing.unidades_por_paquete || 1;
        const cantidadSugerida = ing.cantidad_sugerida || 0;
        
        // Calcular cuántos envases se necesitan (redondear hacia arriba)
        const envasesNecesarios = Math.ceil(cantidadSugerida / unidadesPorEnvase);
        
        // Guardar la cantidad en unidades totales (envases × unidades por envase)
        cant[ing.ingrediente_id] = envasesNecesarios * unidadesPorEnvase;
      });
      setCantidades(cant);
    } catch (error) {
      console.error('Error fetching stock:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProveedores = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/proveedores`,
        {
          headers: { 'Authorization': `Bearer ${getAuthToken()}` }
        }
      );
      const data = await response.json();
      setProveedores(data || []);
    } catch (error) {
      console.error('Error fetching proveedores:', error);
    }
  };

  const fetchAlergenos = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/alergenos`,
        {
          headers: { 'Authorization': `Bearer ${getAuthToken()}` }
        }
      );
      const data = await response.json();
      setAlergenos(data || []);
    } catch (error) {
      console.error('Error fetching alergenos:', error);
    }
  };

  const abrirModalIngrediente = async (ingredienteId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ingredientes/${ingredienteId}`,
        {
          headers: { 'Authorization': `Bearer ${getAuthToken()}` }
        }
      );
      const ingrediente = await response.json();
      setIngredienteSeleccionado(ingrediente);
      setFormData({
        nombre: ingrediente.nombre,
        proveedor_id: ingrediente.proveedor_id || '',
        precio_unitario: ingrediente.precio_unitario || 0,
        unidad: ingrediente.unidad || 'kg',
        precio_paquete: ingrediente.precio_paquete || 0,
        unidades_por_paquete: ingrediente.unidades_por_paquete || 1,
        stock_minimo: ingrediente.stock_minimo || 0,
        tipo_iva: ingrediente.tipo_iva || 10,
        alergenos_ids: ingrediente.alergenos_ids || [],
        carne: ingrediente.carne || false,
        origen_animal: ingrediente.origen_animal || false,
        pasteurizado: ingrediente.pasteurizado || false,
        activo: ingrediente.activo !== undefined ? ingrediente.activo : true
      });
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching ingrediente:', error);
    }
  };

  const cerrarModal = () => {
    setShowModal(false);
    setIngredienteSeleccionado(null);
  };

  const handleGuardarIngrediente = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ingredientes/${ingredienteSeleccionado.id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        }
      );

      if (response.ok) {
        alert('✅ Ingrediente actualizado correctamente');
        cerrarModal();
        fetchStock(); // Refrescar lista
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al actualizar ingrediente');
    }
  };

  const handleSeleccionar = (id) => {
    setSeleccionados(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const seleccionarCriticos = () => {
    const criticos = ingredientes.filter(i => i.estado === 'critico' || i.estado === 'rotura_stock').map(i => i.ingrediente_id);
    setSeleccionados(new Set(criticos));
  };

  const deseleccionarTodos = () => {
    setSeleccionados(new Set());
  };

  const handleCantidadChange = (id, envases, unidadesPorEnvase) => {
    // Calcular cantidad en unidades basándose en envases completos
    const cantidadEnUnidades = (envases || 0) * (unidadesPorEnvase || 1);
    setCantidades(prev => ({
      ...prev,
      [id]: cantidadEnUnidades
    }));
  };

  const incrementarEnvases = (ing) => {
    const unidadesPorEnvase = ing.unidades_por_paquete || 1;
    const cantidadActual = cantidades[ing.ingrediente_id] || 0;
    const envasesActuales = Math.ceil(cantidadActual / unidadesPorEnvase);
    handleCantidadChange(ing.ingrediente_id, envasesActuales + 1, unidadesPorEnvase);
  };

  const decrementarEnvases = (ing) => {
    const unidadesPorEnvase = ing.unidades_por_paquete || 1;
    const cantidadActual = cantidades[ing.ingrediente_id] || 0;
    const envasesActuales = Math.ceil(cantidadActual / unidadesPorEnvase);
    if (envasesActuales > 0) {
      handleCantidadChange(ing.ingrediente_id, envasesActuales - 1, unidadesPorEnvase);
    }
  };

  const generarOrdenes = async () => {
    const ingredientesSeleccionados = ingredientes.filter(i => seleccionados.has(i.ingrediente_id));
    
    if (ingredientesSeleccionados.length === 0) {
      alert('Selecciona al menos un ingrediente');
      return;
    }

    // Agrupar por proveedor
    const porProveedor = {};
    ingredientesSeleccionados.forEach(ing => {
      const provId = ing.proveedor_id || 'sin_proveedor';
      if (!porProveedor[provId]) {
        porProveedor[provId] = {
          proveedor_id: provId,
          proveedor_nombre: ing.proveedor_nombre,
          items: []
        };
      }
      porProveedor[provId].items.push({
        ingrediente_id: ing.ingrediente_id,
        nombre: ing.nombre,
        cantidad_pedida: cantidades[ing.ingrediente_id] || 0,
        unidad: ing.unidad,
        precio_estimado: ing.precio_unitario
      });
    });

    const numProveedores = Object.keys(porProveedor).length;
    const totalItems = ingredientesSeleccionados.length;

    const confirmacion = confirm(
      `Se crearán ${numProveedores} orden(es) de compra con ${totalItems} items.\n¿Continuar?`
    );

    if (!confirmacion) return;

    try {
      setLoading(true);
      let ordenesCreadas = 0;

      for (const [provId, grupo] of Object.entries(porProveedor)) {
        const payload = {
          proveedor_id: provId,
          fecha_entrega_esperada: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          items: grupo.items,
          observaciones: ''
        };

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/ordenes-compra`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${getAuthToken()}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
          }
        );

        if (response.ok) {
          ordenesCreadas++;
        }
      }

      alert(`✅ ${ordenesCreadas} orden(es) creada(s) correctamente`);
      setSeleccionados(new Set());
      fetchStock();
    } catch (error) {
      console.error('Error:', error);
      alert('Error al crear órdenes');
    } finally {
      setLoading(false);
    }
  };

  const ingredientesFiltrados = ingredientes.filter(ing => {
    const matchBusqueda = ing.nombre.toLowerCase().includes(busqueda.toLowerCase());
    const matchEstado = filtroEstado === 'todos' || ing.estado === filtroEstado;
    const matchProveedor = !filtroProveedor || ing.proveedor_id === filtroProveedor;
    return matchBusqueda && matchEstado && matchProveedor;
  });

  // Paginación
  const totalPaginas = Math.ceil(ingredientesFiltrados.length / ingredientesPorPagina);
  const indiceInicio = (paginaActual - 1) * ingredientesPorPagina;
  const indiceFin = indiceInicio + ingredientesPorPagina;
  const ingredientesPaginados = ingredientesFiltrados.slice(indiceInicio, indiceFin);

  const cambiarPagina = (nuevaPagina) => {
    if (nuevaPagina >= 1 && nuevaPagina <= totalPaginas) {
      setPaginaActual(nuevaPagina);
    }
  };

  const stats = {
    criticos: ingredientes.filter(i => i.estado === 'critico').length,
    rotura: ingredientes.filter(i => i.estado === 'rotura_stock').length,
    bajos: ingredientes.filter(i => i.estado === 'bajo').length,
    ok: ingredientes.filter(i => i.estado === 'ok').length
  };

  const proveedoresEnSeleccion = new Set(
    ingredientes.filter(i => seleccionados.has(i.ingrediente_id)).map(i => i.proveedor_id)
  ).size;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-duetto-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Calculando stock proyectado...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Stats Cards - Responsive */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-red-200 p-3 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">Críticos</p>
              <p className="text-xl sm:text-3xl font-bold text-red-600 mt-1">{stats.criticos}</p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-orange-200 p-3 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">Rotura Stock</p>
              <p className="text-xl sm:text-3xl font-bold text-orange-600 mt-1">{stats.rotura}</p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 sm:w-6 sm:h-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-yellow-200 p-3 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">Stock Bajo</p>
              <p className="text-xl sm:text-3xl font-bold text-yellow-600 mt-1">{stats.bajos}</p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 sm:w-6 sm:h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-green-200 p-3 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">Stock OK</p>
              <p className="text-xl sm:text-3xl font-bold text-green-600 mt-1">{stats.ok}</p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-4 h-4 sm:w-6 sm:h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Controles - Responsive */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-6">
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Días adelante</label>
            <select
              value={diasAdelante}
              onChange={(e) => setDiasAdelante(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
            >
              <option value="7">7 días</option>
              <option value="15">15 días</option>
              <option value="30">30 días</option>
              <option value="60">60 días</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
            >
              <option value="todos">Todos</option>
              <option value="critico">Críticos</option>
              <option value="rotura_stock">Rotura Stock</option>
              <option value="bajo">Bajos</option>
              <option value="ok">OK</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Proveedor</label>
            <select
              value={filtroProveedor}
              onChange={(e) => setFiltroProveedor(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
            >
              <option value="">Todos</option>
              {[...proveedores].sort((a, b) => a.nombre.localeCompare(b.nombre)).map(p => (
                <option key={p.id} value={p.id}>{p.nombre}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Buscar</label>
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Nombre ingrediente..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            <strong>{seleccionados.size}</strong> ingredientes de <strong>{proveedoresEnSeleccion}</strong> proveedor(es) seleccionados
          </div>
          <div className="flex gap-2">
            <button
              onClick={seleccionarCriticos}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
            >
              Seleccionar Críticos
            </button>
            <button
              onClick={deseleccionarTodos}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
            >
              Deseleccionar Todos
            </button>
            <button
              onClick={generarOrdenes}
              disabled={seleccionados.size === 0}
              className="px-4 py-2 bg-duetto-primary text-white rounded-lg hover:bg-duetto-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              Generar Órdenes
            </button>
          </div>
        </div>
      </div>

      {/* Tabla de Ingredientes */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">✓</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ingrediente</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proveedor</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Stock Actual</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Necesario ({diasAdelante}d)</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Envases</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ya Pedido</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Stock Proyectado</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Stock Mínimo</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Cantidad a Pedir</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Precio/envase</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {ingredientesPaginados.map((ing) => {
                const isSelected = seleccionados.has(ing.ingrediente_id);
                const getRowColor = () => {
                  if (ing.estado === 'critico') return 'bg-red-50';
                  if (ing.estado === 'rotura_stock') return 'bg-orange-50';
                  if (ing.estado === 'bajo') return 'bg-yellow-50';
                  return '';
                };
                
                const getEstadoBadge = () => {
                  switch (ing.estado) {
                    case 'critico':
                      return <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full"><AlertCircle className="w-3 h-3" /> Crítico</span>;
                    case 'rotura_stock':
                      return <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full"><AlertTriangle className="w-3 h-3" /> Rotura</span>;
                    case 'bajo':
                      return <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full"><AlertTriangle className="w-3 h-3" /> Bajo</span>;
                    default:
                      return <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full"><CheckCircle className="w-3 h-3" /> OK</span>;
                  }
                };

                return (
                  <tr key={ing.ingrediente_id} className={`hover:bg-gray-50 ${getRowColor()}`}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleSeleccionar(ing.ingrediente_id)}
                        className="w-5 h-5 text-duetto-primary rounded focus:ring-2 focus:ring-duetto-primary cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div 
                        onClick={() => abrirModalIngrediente(ing.ingrediente_id)}
                        className="font-medium text-duetto-primary hover:text-duetto-secondary cursor-pointer hover:underline"
                      >
                        {ing.nombre}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{ing.proveedor_nombre}</td>
                    <td className="px-4 py-3 text-center text-gray-900">{ing.stock_actual} {ing.unidad}</td>
                    <td className="px-4 py-3 text-center text-gray-900">{ing.necesario} {ing.unidad}</td>
                    <td className="px-4 py-3 text-center">
                      {(() => {
                        const upe = ing.unidades_por_paquete || 1;
                        const envases = Math.ceil((ing.necesario || 0) / upe);
                        return (
                          <div className="text-gray-900 font-medium" data-testid={`envases-${ing.ingrediente_id}`}>
                            {envases}
                            <div className="text-[10px] text-gray-400">{upe} {ing.unidad}/env</div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-center text-blue-600">{ing.ya_pedido} {ing.unidad}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`font-medium ${ing.stock_proyectado < 0 ? 'text-red-600' : ing.stock_proyectado < ing.stock_minimo ? 'text-orange-600' : 'text-gray-900'}`}>
                        {ing.stock_proyectado} {ing.unidad}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600">{ing.stock_minimo} {ing.unidad}</td>
                    <td className="px-4 py-3 text-center">{getEstadoBadge()}</td>
                    <td className="px-4 py-3">
                      {(() => {
                        const unidadesPorEnvase = ing.unidades_por_paquete || 1;
                        const cantidadActual = cantidades[ing.ingrediente_id] || 0;
                        const envasesActuales = Math.ceil(cantidadActual / unidadesPorEnvase);
                        
                        return (
                          <div className="flex flex-col items-center gap-1">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => decrementarEnvases(ing)}
                                className="w-6 h-6 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded text-gray-700 font-bold text-sm"
                                disabled={envasesActuales <= 0}
                              >
                                -
                              </button>
                              <input
                                type="number"
                                step="1"
                                min="0"
                                value={envasesActuales}
                                onChange={(e) => handleCantidadChange(ing.ingrediente_id, e.target.value === '' ? 0 : parseInt(e.target.value), unidadesPorEnvase)}
                                className="w-14 px-1 py-1 border border-gray-300 rounded text-center text-sm focus:ring-2 focus:ring-duetto-primary"
                              />
                              <button
                                onClick={() => incrementarEnvases(ing)}
                                className="w-6 h-6 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded text-gray-700 font-bold text-sm"
                              >
                                +
                              </button>
                            </div>
                            <div className="text-xs text-gray-500">
                              {cantidadActual.toFixed(2)} {ing.unidad}
                            </div>
                            <div className="text-xs text-gray-400">
                              ({unidadesPorEnvase} uds/envase)
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-900">
                      €{((ing.precio_unitario || 0) * (ing.unidades_por_paquete || 1)).toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {ingredientesFiltrados.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No se encontraron ingredientes con los filtros seleccionados
          </div>
        )}

        {/* Paginación */}
        {totalPaginas > 1 && (
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Mostrando <span className="font-medium">{indiceInicio + 1}</span> a{' '}
              <span className="font-medium">{Math.min(indiceFin, ingredientesFiltrados.length)}</span> de{' '}
              <span className="font-medium">{ingredientesFiltrados.length}</span> ingredientes
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => cambiarPagina(paginaActual - 1)}
                disabled={paginaActual === 1}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <div className="flex gap-1">
                {[...Array(totalPaginas)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => cambiarPagina(i + 1)}
                    className={`px-3 py-1 border rounded-lg text-sm font-medium ${
                      paginaActual === i + 1
                        ? 'bg-duetto-primary text-white border-duetto-primary'
                        : 'text-gray-700 border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
              <button
                onClick={() => cambiarPagina(paginaActual + 1)}
                disabled={paginaActual === totalPaginas}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Detalle/Edición de Ingrediente */}
      {showModal && ingredienteSeleccionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90dvh] overflow-y-auto">
            {/* Modal Header */}
            <div className="p-4 sm:p-6 border-b border-gray-200 bg-duetto-primary bg-opacity-5">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">✏️ Editar Ingrediente</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Modificando: <span className="font-semibold">{ingredienteSeleccionado.nombre}</span>
                  </p>
                </div>
                <button
                  onClick={cerrarModal}
                  type="button"
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <span className="text-gray-500 text-2xl">×</span>
                </button>
              </div>
            </div>

            <form onSubmit={handleGuardarIngrediente} className="p-4 sm:p-6">
              {/* Basic Info Section */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Package className="w-5 h-5 mr-2 text-duetto-primary" />
                  Información Básica
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre del Ingrediente *
                    </label>
                    <input
                      type="text"
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                      placeholder="Ej: Tomate maduro, Pechuga de pollo..."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Proveedor</label>
                    <select
                      value={formData.proveedor_id}
                      onChange={(e) => setFormData({ ...formData, proveedor_id: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                    >
                      <option value="">Sin proveedor asignado</option>
                      {[...proveedores].sort((a, b) => a.nombre.localeCompare(b.nombre)).map(prov => (
                        <option key={prov.id} value={prov.id}>{prov.nombre}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Unidad de Medida *</label>
                    <select
                      value={formData.unidad}
                      onChange={(e) => setFormData({ ...formData, unidad: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                    >
                      <option value="kg">Kilogramos (kg)</option>
                      <option value="g">Gramos (g)</option>
                      <option value="L">Litros (L)</option>
                      <option value="ml">Mililitros (ml)</option>
                      <option value="litros">Litros (litros)</option>
                      <option value="gramos">Gramos (gramos)</option>
                      <option value="unidad">Unidad</option>
                      <option value="ud">Unidad (ud)</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Stock Mínimo de Seguridad
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.stock_minimo}
                      onChange={(e) => setFormData({ ...formData, stock_minimo: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                      onBlur={(e) => { if (e.target.value === '') setFormData({...formData, stock_minimo: 0}); }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                      placeholder="Cantidad mínima para evitar roturas de stock"
                    />
                    <p className="text-xs text-gray-500 mt-1">Cantidad en {formData.unidad} - Aparecerá como &quot;Rotura de Stock&quot; cuando baje de este nivel</p>
                  </div>
                </div>
              </div>

              {/* Pricing Section */}
              <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <span className="text-green-600 mr-2">💰</span>
                  Información de Precios
                </h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    ℹ️ Puedes introducir el precio <strong>con IVA o sin IVA</strong>. El sistema calculará automáticamente el otro valor.
                  </p>
                </div>

                {/* Tipo de IVA primero */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de IVA *
                  </label>
                  <select
                    value={formData.tipo_iva || 10}
                    onChange={(e) => setFormData({ ...formData, tipo_iva: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                    required
                  >
                    <option value="4">4% - Alimentos básicos</option>
                    <option value="10">10% - Alimentos preparados</option>
                    <option value="21">21% - General</option>
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Precio Envase SIN IVA (€) *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.precio_paquete ? parseFloat(formData.precio_paquete).toFixed(2) : ''}
                      onChange={(e) => setFormData({ ...formData, precio_paquete: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                      placeholder="0.00"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Precio sin IVA</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Precio Envase CON IVA (€)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.precio_paquete ? (parseFloat(formData.precio_paquete) * (1 + (formData.tipo_iva || 10) / 100)).toFixed(2) : ''}
                      onChange={(e) => {
                        const precioConIva = parseFloat(e.target.value) || 0;
                        const precioSinIva = precioConIva / (1 + (formData.tipo_iva || 10) / 100);
                        const precioRedondeado = Math.round(precioSinIva * 100) / 100;
                        setFormData({ ...formData, precio_paquete: precioRedondeado.toString() });
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-emerald-50"
                      placeholder="0.00"
                    />
                    <p className="text-xs text-gray-500 mt-1">Precio con IVA ({formData.tipo_iva || 10}%)</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Unidades por Envase *
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0.1"
                      value={formData.unidades_por_paquete}
                      onChange={(e) => setFormData({ ...formData, unidades_por_paquete: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                      placeholder="1"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Cantidad en {formData.unidad}</p>
                  </div>
                </div>
                
                {/* Price Calculation Helper */}
                {formData.precio_paquete > 0 && formData.unidades_por_paquete > 0 && (
                  <div className="mt-4 p-4 bg-white rounded-lg border border-green-300">
                    <p className="text-sm text-gray-700 mb-2">
                      <span className="font-semibold">📊 Precio calculado por unidad:</span>{' '}
                      <span className="text-green-700 font-bold">€{(parseFloat(formData.precio_paquete) / parseFloat(formData.unidades_por_paquete)).toFixed(4)} / {formData.unidad}</span> (sin IVA)
                    </p>
                  </div>
                )}
              </div>

              {/* Special Characteristics Section */}
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Package className="w-5 h-5 mr-2 text-blue-600" />
                  Características Especiales
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <label className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-all ${
                    formData.carne ? 'bg-red-100 border-red-400' : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}>
                    <input
                      type="checkbox"
                      checked={formData.carne}
                      onChange={(e) => setFormData({ ...formData, carne: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-duetto-primary focus:ring-duetto-primary"
                    />
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900">Contiene carne</span>
                    </div>
                  </label>

                  <label className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-all ${
                    formData.origen_animal ? 'bg-orange-100 border-orange-400' : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}>
                    <input
                      type="checkbox"
                      checked={formData.origen_animal}
                      onChange={(e) => setFormData({ ...formData, origen_animal: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-duetto-primary focus:ring-duetto-primary"
                    />
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900">Origen animal</span>
                    </div>
                  </label>

                  <label className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-all ${
                    formData.pasteurizado ? 'bg-blue-100 border-blue-400' : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}>
                    <input
                      type="checkbox"
                      checked={formData.pasteurizado}
                      onChange={(e) => setFormData({ ...formData, pasteurizado: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-duetto-primary focus:ring-duetto-primary"
                    />
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900">Pasteurizado</span>
                    </div>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Estas características ayudan a identificar restricciones alimentarias
                </p>
              </div>

              {/* Allergens Section */}
              <div className="mb-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2 text-amber-600" />
                  Alérgenos
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {alergenos.map(alergeno => (
                    <label 
                      key={alergeno.id} 
                      className={`flex items-center space-x-2 p-3 border rounded-lg cursor-pointer transition-all ${
                        formData.alergenos_ids.includes(alergeno.id)
                          ? 'bg-amber-100 border-amber-400 shadow-sm ring-2 ring-amber-300'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.alergenos_ids.includes(alergeno.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, alergenos_ids: [...formData.alergenos_ids, alergeno.id] });
                          } else {
                            setFormData({ ...formData, alergenos_ids: formData.alergenos_ids.filter(id => id !== alergeno.id) });
                          }
                        }}
                        className="rounded border-gray-300 text-duetto-primary focus:ring-duetto-primary"
                      />
                      <span className="text-xs font-medium text-gray-900">{alergeno.nombre}</span>
                    </label>
                  ))}
                </div>
                {formData.alergenos_ids.length === 0 && (
                  <p className="text-sm text-gray-500 mt-2">
                    ✓ Este ingrediente no contiene alérgenos registrados
                  </p>
                )}
              </div>

              {/* Status Section */}
              <div className="mb-6">
                <label className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.activo}
                    onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300 text-duetto-primary focus:ring-duetto-primary"
                  />
                  <div>
                    <span className="text-sm font-semibold text-gray-900">Ingrediente activo</span>
                    <p className="text-xs text-gray-500">Los ingredientes inactivos no aparecerán en las recetas</p>
                  </div>
                </label>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={cerrarModal}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-duetto-primary text-white rounded-lg hover:bg-duetto-secondary font-medium transition-colors"
                >
                  Guardar Cambios
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Sub-Tab de Órdenes de Compra (código original)
const SubTabOrdenes = ({ abrirModalEnvioEmail, ordenDestacada = null }) => {
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const refDestacada = useRef(null);

  // Orden destacada (?orden=<id>): se lleva a la vista al cargar la lista
  useEffect(() => {
    if (!ordenDestacada || loading || !refDestacada.current) return;
    refDestacada.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [ordenDestacada, loading, ordenes]);
  const [ordenEditando, setOrdenEditando] = useState(null);
  const [showModalEditar, setShowModalEditar] = useState(false);

  useEffect(() => {
    fetchOrdenes();
  }, [filtroEstado]);

  const fetchOrdenes = async () => {
    setLoading(true);
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/ordenes-compra`;
      if (filtroEstado !== 'todos') {
        url += `?estado=${filtroEstado}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });
      const data = await response.json();
      setOrdenes(data || []);
    } catch (error) {
      console.error('Error fetching ordenes:', error);
    } finally {
      setLoading(false);
    }
  };

  const confirmarOrden = async (ordenId) => {
    const confirmacion = confirm(
      '⚠️ ¿Enviar este pedido al proveedor?\n\n' +
      'Una vez enviado:\n' +
      '- No podrás editarlo\n' +
      '- El proveedor recibirá la orden\n' +
      '- Solo podrás cancelarlo'
    );

    if (!confirmacion) return;

    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ordenes-compra/${ordenId}/confirmar`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`
          }
        }
      );

      if (response.ok) {
        alert('✅ Pedido enviado al proveedor exitosamente');
        fetchOrdenes(); // Refrescar lista
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al enviar pedido');
    } finally {
      setLoading(false);
    }
  };

  const eliminarOrden = async (ordenId) => {
    const confirmacion = confirm(
      '⚠️ ¿Eliminar esta orden de compra?\n\n' +
      'Esta acción no se puede deshacer.\n' +
      'La orden será eliminada permanentemente.'
    );

    if (!confirmacion) return;

    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ordenes-compra/${ordenId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`
          }
        }
      );

      if (response.ok) {
        alert('✅ Orden eliminada exitosamente');
        fetchOrdenes(); // Refrescar lista
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al eliminar orden');
    } finally {
      setLoading(false);
    }
  };

  const descargarPDF = async (ordenId) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ordenes-compra/${ordenId}/pdf`,
        {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`
          }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `orden_compra_${ordenId.substring(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        alert('✅ PDF descargado exitosamente');
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al descargar PDF');
    } finally {
      setLoading(false);
    }
  };

  const [showModalEnvioEmail, setShowModalEnvioEmail] = useState(false);
  const [ordenParaEnviar, setOrdenParaEnviar] = useState(null);

  const enviarEmailOrden = async () => {
    if (!ordenParaEnviar) return;
    
    const ordenId = ordenParaEnviar.id;

    try {
      setLoading(true);
      
      // Primero enviar el email
      const emailResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ordenes-compra/${ordenId}/enviar-email`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`
          }
        }
      );

      if (!emailResponse.ok) {
        const error = await emailResponse.json();
        alert(`Error al enviar email: ${error.detail}`);
        return;
      }

      // Luego confirmar la orden (cambiar estado a "solicitado")
      const confirmarResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ordenes-compra/${ordenId}/confirmar`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`
          }
        }
      );

      if (confirmarResponse.ok) {
        const emailResult = await emailResponse.json();
        const log = emailResult.log_envio;
        
        alert(
          `✅ Email enviado exitosamente\n\n` +
          `📧 Destinatario: ${log.destinatario_nombre} (${log.destinatario})\n` +
          `📅 Fecha: ${log.fecha_envio} a las ${log.hora_envio}\n` +
          `📝 Asunto: ${log.asunto}\n` +
          `👤 Enviado por: ${log.enviado_por}`
        );
        
        setShowModalEnvioEmail(false);
        setOrdenParaEnviar(null);
        fetchOrdenes(); // Refrescar lista
      } else {
        const error = await confirmarResponse.json();
        alert(`Email enviado pero error al confirmar: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error en el proceso de envío');
    } finally {
      setLoading(false);
    }
  };

  const abrirEdicionOrden = (orden) => {
    // Crear una copia profunda de la orden para editar
    setOrdenEditando({
      ...orden,
      items: orden.items.map(item => ({...item}))
    });
    setShowModalEditar(true);
  };

  const guardarEdicionOrden = async () => {
    if (!ordenEditando) return;

    try {
      setLoading(true);
      
      // Preparar datos para el backend
      const ordenData = {
        proveedor_id: ordenEditando.proveedor_id,
        fecha_entrega_esperada: ordenEditando.fecha_entrega_esperada,
        observaciones: ordenEditando.observaciones || '',
        items: ordenEditando.items.map(item => ({
          ingrediente_id: item.ingrediente_id,
          cantidad_pedida: parseFloat(item.cantidad_pedida) || 0,
          unidad: item.unidad,
          precio_estimado: parseFloat(item.precio_estimado) || 0
        }))
      };

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ordenes-compra/${ordenEditando.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAuthToken()}`
          },
          body: JSON.stringify(ordenData)
        }
      );

      if (response.ok) {
        alert('✅ Orden actualizada exitosamente');
        setShowModalEditar(false);
        setOrdenEditando(null);
        fetchOrdenes();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Error al actualizar orden'}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al actualizar orden');
    } finally {
      setLoading(false);
    }
  };

  const actualizarItemOrden = (index, campo, valor) => {
    setOrdenEditando(prev => {
      const nuevosItems = [...prev.items];
      const item = {...nuevosItems[index]};
      
      if (campo === 'cantidad_envases') {
        // Si se modifica el número de envases, debe ser un número entero válido
        let envases = parseInt(valor);
        
        // Validar que sea al menos 1
        if (isNaN(envases) || envases < 1) {
          envases = 1;
        }
        
        const unidadesPorEnvase = item.unidades_por_envase || 1;
        item.cantidad_envases = envases;
        item.cantidad_pedida = envases * unidadesPorEnvase;
      } else {
        item[campo] = valor;
      }
      
      nuevosItems[index] = item;
      
      // Recalcular total usando la cantidad_pedida (total de unidades)
      const nuevoTotal = nuevosItems.reduce((sum, it) => {
        const cantidad = parseFloat(it.cantidad_pedida) || 0;
        const precio = parseFloat(it.precio_estimado) || 0;
        return sum + (cantidad * precio);
      }, 0);
      
      return {
        ...prev,
        items: nuevosItems,
        total_estimado: nuevoTotal
      };
    });
  };

  const eliminarItemOrden = (index) => {
    setOrdenEditando(prev => {
      const nuevosItems = prev.items.filter((_, i) => i !== index);
      
      // Recalcular total
      const nuevoTotal = nuevosItems.reduce((sum, item) => {
        const cantidad = parseFloat(item.cantidad_pedida) || 0;
        const precio = parseFloat(item.precio_estimado) || 0;
        return sum + (cantidad * precio);
      }, 0);
      
      return {
        ...prev,
        items: nuevosItems,
        total_estimado: nuevoTotal
      };
    });
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'pendiente':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      case 'solicitado':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'recibido_parcial':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'recibido':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'cancelado':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getEstadoTexto = (estado) => {
    switch (estado) {
      case 'pendiente':
        return '📝 Pendiente';
      case 'solicitado':
        return '📤 Solicitado';
      case 'recibido_parcial':
        return 'Parcial';
      case 'recibido':
        return 'Recibido';
      case 'cancelado':
        return 'Cancelado';
      default:
        return estado;
    }
  };

  const stats = {
    total: ordenes.length,
    pendientes: ordenes.filter(o => o.estado === 'pendiente' && !o.confirmado).length,
    solicitadas: ordenes.filter(o => o.estado === 'solicitado' || o.confirmado).length,
    parciales: ordenes.filter(o => o.estado === 'recibido_parcial').length,
    recibidas: ordenes.filter(o => o.estado === 'recibido').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-duetto-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando órdenes de compra...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Órdenes</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <ShoppingCart className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pendientes</p>
              <p className="text-3xl font-bold text-gray-600 mt-1">{stats.pendientes}</p>
            </div>
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-gray-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-yellow-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Solicitadas</p>
              <p className="text-3xl font-bold text-yellow-600 mt-1">{stats.solicitadas}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Truck className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Parciales</p>
              <p className="text-3xl font-bold text-blue-600 mt-1">{stats.parciales}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-green-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Recibidas</p>
              <p className="text-3xl font-bold text-green-600 mt-1">{stats.recibidas}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Alerta de Respuestas Pendientes */}
      {ordenes.filter(o => o.tiene_respuestas_sin_leer).length > 0 && (
        <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 shadow-sm animate-pulse">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-red-800">
                🚨 {ordenes.filter(o => o.tiene_respuestas_sin_leer).length} {ordenes.filter(o => o.tiene_respuestas_sin_leer).length === 1 ? 'orden tiene' : 'órdenes tienen'} respuestas del proveedor sin revisar
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p className="mb-2">Algunos proveedores han respondido a tus pedidos. Puede haber cambios en:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Disponibilidad de productos</li>
                  <li>Cantidades o precios</li>
                  <li>Fechas de entrega</li>
                </ul>
              </div>
              <div className="mt-3">
                <button
                  onClick={() => setFiltroEstado('todos')}
                  className="text-sm font-medium text-red-800 hover:text-red-900 underline"
                >
                  Ver órdenes con respuestas →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Órdenes de Compra</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setFiltroEstado('todos')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filtroEstado === 'todos' ? 'bg-duetto-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Todas ({stats.total})
            </button>
            <button
              onClick={() => setFiltroEstado('pendiente')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filtroEstado === 'pendiente' ? 'bg-gray-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Pendientes ({stats.pendientes})
            </button>
            <button
              onClick={() => setFiltroEstado('solicitado')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filtroEstado === 'solicitado' ? 'bg-yellow-600 text-white' : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
              }`}
            >
              Solicitadas ({stats.solicitadas})
            </button>
            <button
              onClick={() => setFiltroEstado('recibido_parcial')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filtroEstado === 'recibido_parcial' ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
            >
              Parciales ({stats.parciales})
            </button>
            <button
              onClick={() => setFiltroEstado('recibido')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filtroEstado === 'recibido' ? 'bg-green-600 text-white' : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              Recibidas ({stats.recibidas})
            </button>
          </div>
        </div>
      </div>

      {/* Lista de Órdenes */}
      <div className="space-y-4">
        {ordenes.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <ShoppingCart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay órdenes</h3>
            <p className="text-gray-600">No se encontraron órdenes de compra con los filtros seleccionados</p>
          </div>
        ) : (
          ordenes.map((orden) => (
            <div
              key={orden.id}
              ref={orden.id === ordenDestacada ? refDestacada : null}
              className={`bg-white rounded-xl shadow-sm border overflow-hidden ${orden.id === ordenDestacada ? 'border-duetto-primary ring-2 ring-duetto-primary' : 'border-gray-200'}`}
              data-testid={orden.id === ordenDestacada ? 'orden-destacada' : undefined}
            >
              {/* Header de la Orden */}
              <div className="bg-gradient-to-r from-duetto-primary to-duetto-secondary text-white p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                      <Truck className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold">
                        {orden.proveedor_nombre}
                        {orden.numero && <span className="ml-2 text-sm font-semibold text-white/80">Orden {orden.numero}</span>}
                      </h3>
                      <p className="text-sm text-white/80">
                        Pedido: {new Date(orden.fecha_pedido).toLocaleDateString('es-ES')}
                        {orden.pedido_por_dia
                          ? ` | Entregas: ${(orden.fechas_entrega || []).map(ddmm).join(', ')}`
                          : (orden.modo_suministro === 'compra_directa'
                            ? ` | Compra: ${new Date(orden.fecha_compra || orden.fecha_entrega_esperada).toLocaleDateString('es-ES')}`
                            : (orden.fecha_entrega_esperada ? ` | Entrega: ${new Date(orden.fecha_entrega_esperada).toLocaleDateString('es-ES')}` : ''))}
                        {orden.modo_suministro === 'compra_directa' && ' | Compra directa (lista para ir a comprar)'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border ${getEstadoColor(orden.estado)}`}>
                        {orden.modo_suministro === 'compra_directa' && orden.estado === 'recibido' ? 'Comprado' : getEstadoTexto(orden.estado)}
                      </span>
                      {orden.tiene_respuestas_sin_leer && (
                        <span className="relative flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" title="Respuesta del proveedor sin leer"></span>
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-white/80 mt-1">
                      {orden.items?.length || 0} items
                    </p>
                    {orden.tiene_respuestas_sin_leer && (
                      <p className="text-xs text-red-200 font-medium mt-1 animate-pulse">
                        🚨 Respuesta del proveedor
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Detalles de la Orden */}
              <div className="p-4">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ingrediente</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Cantidad Pedida</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Cantidad Recibida</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Precio Unit.</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {orden.items?.map((item, idx) => {
                        const recibida = item.cantidad_recibida || 0;
                        const pedida = item.cantidad_pedida || 0;
                        const porcentaje = pedida > 0 ? (recibida / pedida) * 100 : 0;
                        
                        return (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900">{item.nombre}</div>
                              {(item.entregas_por_dia || []).length > 0 && (
                                <div className="mt-0.5 text-xs text-gray-500" data-testid={`orden-entregas-${item.ingrediente_id}`}>
                                  {item.entregas_por_dia.map(e => {
                                    const upe = Number(item.unidades_por_envase) || 1;
                                    const cantidad = upe !== 1
                                      ? `${e.cantidad_envases} paq. de ${upe} ${item.unidad || ''}`
                                      : `${e.cantidad_pedida} ${item.unidad || ''}`;
                                    return <div key={`${e.fecha_evento}-${e.fecha_entrega}`}>Entrega {textoDia(e.fecha_entrega)}: {cantidad.trim()}</div>;
                                  })}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center text-gray-900">
                              {pedida} {item.unidad}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`font-medium ${porcentaje === 100 ? 'text-green-600' : porcentaje > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                                {recibida} {item.unidad}
                              </span>
                              {porcentaje > 0 && (
                                <span className="text-xs text-gray-500 ml-1">({porcentaje.toFixed(0)}%)</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-600">
                              €{(item.precio_estimado || 0).toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-right font-medium text-gray-900">
                              €{((item.cantidad_pedida || 0) * (item.precio_estimado || 0)).toFixed(2)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan="4" className="px-4 py-3 text-right font-bold text-gray-900">Total Estimado:</td>
                        <td className="px-4 py-3 text-right font-bold text-gray-900">
                          €{(orden.total_estimado || 0).toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {orden.observaciones && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600"><strong>Observaciones:</strong> {orden.observaciones}</p>
                  </div>
                )}

                {/* Botones de Acción */}
                <div className="mt-4 flex items-center justify-end gap-3">
                  {/* Botón Descargar PDF - siempre visible */}
                  <button
                    onClick={() => descargarPDF(orden.id)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                    title="Descargar PDF"
                  >
                    <Package className="w-4 h-4" />
                    Descargar PDF
                  </button>
                  
                  {orden.estado === 'pendiente' && !orden.confirmado ? (
                    <>
                      <button
                        onClick={() => eliminarOrden(orden.id)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                      >
                        <Trash2 className="w-4 h-4" />
                        Eliminar
                      </button>
                      <button
                        onClick={() => abrirEdicionOrden(orden)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                      >
                        <Edit2 className="w-4 h-4" />
                        Editar Pedido
                      </button>
                      {orden.modo_suministro !== 'compra_directa' && (
                        <button
                          onClick={() => abrirModalEnvioEmail(orden)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Enviar a Proveedor
                        </button>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={() => abrirEdicionOrden(orden)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                    >
                      <Eye className="w-4 h-4" />
                      Ver Detalles
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal de Edición/Vista */}
      {showModalEditar && ordenEditando && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90dvh] overflow-hidden flex flex-col">
            {/* Header del Modal */}
            <div className="bg-gradient-to-r from-duetto-primary to-duetto-secondary text-white p-4 sm:p-6 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-2xl font-bold">
                  {(ordenEditando.estado === 'pendiente' || ordenEditando.estado === 'solicitado') && !ordenEditando.confirmado ? '✏️ Editar Orden de Compra' : '👁️ Ver Orden de Compra'}
                </h2>
                {ordenEditando.estado === 'solicitado' && ordenEditando.tiene_respuestas_sin_leer && (
                  <p className="text-yellow-200 text-sm mt-1 font-medium">
                    ⚠️ Ajusta las cantidades según la respuesta del proveedor
                  </p>
                )}
                <p className="text-white/80 text-sm mt-1">
                  {ordenEditando.proveedor_nombre} | Pedido: {new Date(ordenEditando.fecha_pedido).toLocaleDateString('es-ES')}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowModalEditar(false);
                  setOrdenEditando(null);
                }}
                className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Contenido del Modal */}
            <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6">
              {/* Información General */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha Entrega Esperada
                  </label>
                  <input
                    type="date"
                    value={ordenEditando.fecha_entrega_esperada || ''}
                    onChange={(e) => setOrdenEditando({...ordenEditando, fecha_entrega_esperada: e.target.value})}
                    disabled={ordenEditando.estado !== 'pendiente' && ordenEditando.estado !== 'solicitado'}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estado
                  </label>
                  <span className={`inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium border ${getEstadoColor(ordenEditando.estado)}`}>
                    {getEstadoTexto(ordenEditando.estado)}
                  </span>
                </div>
              </div>

              {/* Tabla de Items */}
              <div className="mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Items del Pedido</h3>
                <div className="border border-gray-200 rounded-lg overflow-x-auto">
                  <table className="w-full min-w-[640px]">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ingrediente</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Necesarias</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Envases</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total Uds</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Precio Unit.</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Subtotal</th>
                        {((ordenEditando.estado === 'pendiente' || ordenEditando.estado === 'solicitado') && !ordenEditando.confirmado) && (
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Acciones</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {ordenEditando.items.map((item, idx) => {
                        const cantidadNecesaria = item.cantidad_necesaria || item.cantidad_pedida || 0;
                        const unidadesPorEnvase = item.unidades_por_envase || 1;
                        const cantidadEnvases = item.cantidad_envases || Math.ceil(cantidadNecesaria / unidadesPorEnvase);
                        const totalUnidades = cantidadEnvases * unidadesPorEnvase;
                        
                        return (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900">{item.nombre}</div>
                              <div className="text-sm text-gray-500">{item.unidad}</div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-center">
                                <div className="font-medium text-gray-900">{cantidadNecesaria.toFixed(2)}</div>
                                <div className="text-xs text-gray-500">{item.unidad}</div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              {((ordenEditando.estado === 'pendiente' || ordenEditando.estado === 'solicitado') && !ordenEditando.confirmado) ? (
                                <div className="flex flex-col items-center gap-2">
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => actualizarItemOrden(idx, 'cantidad_envases', Math.max(1, cantidadEnvases - 1))}
                                      className="w-10 h-10 sm:w-7 sm:h-7 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded text-gray-700 font-bold"
                                      aria-label="Un envase menos"
                                      disabled={cantidadEnvases <= 1}
                                    >
                                      -
                                    </button>
                                    <input
                                      type="number"
                                      step="1"
                                      min="1"
                                      value={cantidadEnvases}
                                      inputMode="decimal"
                                      onChange={(e) => actualizarItemOrden(idx, 'cantidad_envases', e.target.value)}
                                      className="w-20 px-2 py-1 text-center border border-gray-300 rounded focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                                    />
                                    <button
                                      onClick={() => actualizarItemOrden(idx, 'cantidad_envases', cantidadEnvases + 1)}
                                      className="w-10 h-10 sm:w-7 sm:h-7 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded text-gray-700 font-bold"
                                      aria-label="Un envase más"
                                    >
                                      +
                                    </button>
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {unidadesPorEnvase} uds/envase
                                  </div>
                                </div>
                              ) : (
                                <div className="text-center">
                                  <div className="font-medium text-gray-900">{cantidadEnvases}</div>
                                  <div className="text-xs text-gray-500">{unidadesPorEnvase} uds/envase</div>
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-center">
                                <div className={`font-medium ${totalUnidades > cantidadNecesaria ? 'text-blue-600' : 'text-gray-900'}`}>
                                  {totalUnidades.toFixed(2)}
                                </div>
                                {totalUnidades > cantidadNecesaria && (
                                  <div className="text-xs text-blue-600">
                                    +{(totalUnidades - cantidadNecesaria).toFixed(2)} extra
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              {((ordenEditando.estado === 'pendiente' || ordenEditando.estado === 'solicitado') && !ordenEditando.confirmado) ? (
                                <div className="flex items-center justify-center">
                                  <span className="text-gray-500 mr-1">€</span>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={item.precio_estimado}
                                    onChange={(e) => actualizarItemOrden(idx, 'precio_estimado', e.target.value)}
                                    className="w-20 px-2 py-1 text-center border border-gray-300 rounded focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                                  />
                                </div>
                              ) : (
                                <div className="text-center text-gray-600">€{(item.precio_estimado || 0).toFixed(2)}</div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right font-medium text-gray-900">
                              €{(totalUnidades * (parseFloat(item.precio_estimado) || 0)).toFixed(2)}
                            </td>
                            {((ordenEditando.estado === 'pendiente' || ordenEditando.estado === 'solicitado') && !ordenEditando.confirmado) && (
                              <td className="px-4 py-3 text-center">
                                <button
                                  onClick={() => eliminarItemOrden(idx)}
                                  className="text-red-600 hover:text-red-700 p-1 rounded hover:bg-red-50"
                                  title="Eliminar item"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan={((ordenEditando.estado === 'pendiente' || ordenEditando.estado === 'solicitado') && !ordenEditando.confirmado) ? "5" : "5"} className="px-4 py-3 text-right font-bold text-gray-900">
                          Total Estimado:
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-gray-900">
                          €{(ordenEditando.total_estimado || 0).toFixed(2)}
                        </td>
                        {((ordenEditando.estado === 'pendiente' || ordenEditando.estado === 'solicitado') && !ordenEditando.confirmado) && <td></td>}
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Observaciones */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Observaciones
                </label>
                <textarea
                  value={ordenEditando.observaciones || ''}
                  onChange={(e) => setOrdenEditando({...ordenEditando, observaciones: e.target.value})}
                  disabled={ordenEditando.estado !== 'pendiente' && ordenEditando.estado !== 'solicitado'}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent disabled:bg-gray-100"
                  placeholder="Notas adicionales sobre el pedido..."
                />
              </div>

              {/* Historial de Comunicaciones (si la orden ha sido enviada) */}
              {ordenEditando.envio_email && (
                <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Historial de Comunicaciones
                  </h3>
                  <div className="space-y-2">
                    <div className="bg-white p-3 rounded border border-blue-100">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-xs font-medium text-gray-900">
                            📤 Enviado el {ordenEditando.envio_email.fecha_envio} a las {ordenEditando.envio_email.hora_envio}
                          </p>
                          <p className="text-xs text-gray-600 mt-1">
                            <strong>Para:</strong> {ordenEditando.envio_email.destinatario} ({ordenEditando.envio_email.destinatario_nombre})
                          </p>
                          <p className="text-xs text-gray-600">
                            <strong>Asunto:</strong> {ordenEditando.envio_email.asunto}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Enviado por: {ordenEditando.envio_email.enviado_por}
                          </p>
                        </div>
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                          Enviado
                        </span>
                      </div>
                    </div>
                    
                    {ordenEditando.respuestas_proveedor && ordenEditando.respuestas_proveedor.length > 0 ? (
                      <>
                        {/* Alerta si hay respuestas sin leer */}
                        {ordenEditando.tiene_respuestas_sin_leer && (
                          <div className="bg-red-50 border-l-4 border-red-400 p-3 mb-2">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start">
                                <div className="flex-shrink-0">
                                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                </div>
                                <div className="ml-3">
                                  <p className="text-sm font-medium text-red-800">
                                    ⚠️ El proveedor ha respondido - Revisa y ajusta cantidades si es necesario
                                  </p>
                                </div>
                              </div>
                              <button
                                onClick={async () => {
                                  try {
                                    const response = await fetch(
                                      `${process.env.REACT_APP_BACKEND_URL}/api/ordenes-compra/${ordenEditando.id}/marcar-respuestas-leidas`,
                                      {
                                        method: 'POST',
                                        headers: {
                                          'Authorization': `Bearer ${getAuthToken()}`
                                        }
                                      }
                                    );
                                    if (response.ok) {
                                      setOrdenEditando({
                                        ...ordenEditando,
                                        tiene_respuestas_sin_leer: false
                                      });
                                      fetchOrdenes();
                                    }
                                  } catch (error) {
                                    console.error('Error marcando como leído:', error);
                                  }
                                }}
                                className="text-xs px-3 py-1 bg-red-100 hover:bg-red-200 text-red-800 rounded transition-colors font-medium"
                              >
                                Marcar como leído
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {ordenEditando.respuestas_proveedor.map((respuesta, idx) => (
                          <div key={idx} className={`p-3 rounded border ${ordenEditando.tiene_respuestas_sin_leer ? 'bg-yellow-50 border-yellow-200' : 'bg-white border-green-100'}`}>
                            <p className="text-xs font-medium text-gray-900">
                              📥 Respuesta recibida el {respuesta.fecha}
                            </p>
                            <p className="text-xs text-gray-600 mt-1">
                              <strong>De:</strong> {respuesta.from}
                            </p>
                            <p className="text-xs text-gray-600">
                              <strong>Asunto:</strong> {respuesta.subject}
                            </p>
                            {respuesta.mensaje && (
                              <div className="mt-2 p-2 bg-white border border-gray-200 rounded">
                                <p className="text-xs font-medium text-gray-700 mb-1">💬 Mensaje del proveedor:</p>
                                <p className="text-xs text-gray-800 whitespace-pre-wrap">
                                  {respuesta.mensaje}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </>
                    ) : (
                      <p className="text-xs text-gray-500 italic">
                        Esperando respuesta del proveedor...
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer del Modal */}
            <div className="border-t border-gray-200 p-6 bg-gray-50 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowModalEditar(false);
                  setOrdenEditando(null);
                }}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                {((ordenEditando.estado === 'pendiente' || ordenEditando.estado === 'solicitado') && !ordenEditando.confirmado) ? 'Cancelar' : 'Cerrar'}
              </button>
              {((ordenEditando.estado === 'pendiente' || ordenEditando.estado === 'solicitado') && !ordenEditando.confirmado) && (
                <button
                  onClick={guardarEdicionOrden}
                  className="px-6 py-2 bg-duetto-primary text-white rounded-lg hover:bg-duetto-primary/90 transition-colors font-medium"
                >
                  Guardar Cambios
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Componente para Tab "Alertas de Precio"
const TabAlertasPrecio = ({ onCountUpdate }) => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [alertas, setAlertas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('pendiente');
  const [resolviendoId, setResolviendoId] = useState(null);
  const [notasResolucion, setNotasResolucion] = useState({});
  const [userRol, setUserRol] = useState('');

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const u = JSON.parse(storedUser);
        setUserRol(u.rol || '');
      }
    } catch (e) { /* ignore */ }
  }, []);

  const cargarAlertas = async () => {
    try {
      setLoading(true);
      const url = filtroEstado === 'todas'
        ? `${API_URL}/api/alertas-precio`
        : `${API_URL}/api/alertas-precio?estado=${filtroEstado}`;
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAlertas(data);
      }
    } catch (err) {
      console.error('Error cargando alertas:', err);
    } finally {
      setLoading(false);
    }
  };

  const cargarConteo = async () => {
    try {
      const res = await fetch(`${API_URL}/api/alertas-precio/pendientes/count`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        onCountUpdate?.(data.count || 0);
      }
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    cargarAlertas();
    cargarConteo();
  }, [filtroEstado]);

  const resolverAlerta = async (alertaId, accion) => {
    try {
      setResolviendoId(alertaId);
      const res = await fetch(`${API_URL}/api/alertas-precio/${alertaId}/resolver`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          accion,
          actualizar_catalogo: accion === 'aprobar',
          notas: notasResolucion[alertaId] || null
        })
      });
      if (res.ok) {
        cargarAlertas();
        cargarConteo();
        setNotasResolucion(prev => { const n = {...prev}; delete n[alertaId]; return n; });
      } else {
        const err = await res.json();
        alert(err.detail || 'Error al resolver la alerta');
      }
    } catch (err) {
      console.error('Error resolviendo alerta:', err);
      alert('Error de conexión al resolver la alerta');
    } finally {
      setResolviendoId(null);
    }
  };

  const puedeResolver = userRol === 'direccion';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-duetto-primary"></div>
        <span className="ml-3 text-gray-500">Cargando alertas...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="tab-alertas-precio">
      {/* Filtros */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-600">Filtrar:</span>
          <div className="flex rounded-lg overflow-hidden border border-gray-200">
            {[
              { value: 'pendiente', label: 'Pendientes' },
              { value: 'aprobado', label: 'Aprobadas' },
              { value: 'rechazado', label: 'Rechazadas' },
              { value: 'todas', label: 'Todas' }
            ].map(f => (
              <button
                key={f.value}
                onClick={() => setFiltroEstado(f.value)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  filtroEstado === f.value
                    ? 'bg-duetto-primary text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                data-testid={`filtro-alertas-${f.value}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="text-sm text-gray-500">
          {alertas.length} alerta{alertas.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Info para roles sin permiso */}
      {!puedeResolver && filtroEstado === 'pendiente' && alertas.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-700">
            Solo el rol de <strong>Dirección</strong> puede aprobar o rechazar cambios de precio.
          </p>
        </div>
      )}

      {/* Lista vacía */}
      {alertas.length === 0 && (
        <div className="text-center py-12">
          <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">
            {filtroEstado === 'pendiente' 
              ? 'No hay alertas de precio pendientes' 
              : `No hay alertas ${filtroEstado === 'todas' ? '' : filtroEstado + 's'}`
            }
          </p>
          <p className="text-sm text-gray-400 mt-1">
            Las alertas se generan automáticamente al recibir mercancía con precios diferentes al catálogo.
          </p>
        </div>
      )}

      {/* Lista de alertas */}
      <div className="space-y-3">
        {alertas.map(alerta => {
          const subida = alerta.precio_nuevo > alerta.precio_anterior;
          const diff = ((alerta.precio_nuevo - alerta.precio_anterior) / alerta.precio_anterior * 100);
          const esPendiente = alerta.estado === 'pendiente';

          return (
            <div
              key={alerta.id}
              className={`border rounded-xl p-4 transition-all ${
                esPendiente
                  ? 'border-amber-200 bg-white shadow-sm hover:shadow-md'
                  : alerta.estado === 'aprobado'
                    ? 'border-green-200 bg-green-50/50'
                    : 'border-red-200 bg-red-50/50'
              }`}
              data-testid={`alerta-precio-${alerta.id}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                {/* Info principal */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg flex-shrink-0 ${
                      subida ? 'bg-red-100' : 'bg-green-100'
                    }`}>
                      {subida 
                        ? <ArrowUpRight className="w-5 h-5 text-red-600" />
                        : <ArrowDownRight className="w-5 h-5 text-green-600" />
                      }
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-semibold text-gray-900 truncate">{alerta.ingrediente_nombre}</h4>
                      <p className="text-sm text-gray-500 mt-0.5">
                        Proveedor: {alerta.proveedor_nombre || 'Sin proveedor'}
                      </p>
                    </div>
                  </div>

                  {/* Precios */}
                  <div className="flex items-center gap-4 mt-3 flex-wrap">
                    <div className="text-sm">
                      <span className="text-gray-500">Catálogo:</span>{' '}
                      <span className="font-medium text-gray-700">€{alerta.precio_anterior?.toFixed(2)}</span>
                    </div>
                    <span className="text-gray-300">→</span>
                    <div className="text-sm">
                      <span className="text-gray-500">Recibido:</span>{' '}
                      <span className={`font-semibold ${subida ? 'text-red-600' : 'text-green-600'}`}>
                        €{alerta.precio_nuevo?.toFixed(2)}
                      </span>
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${
                      subida ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {subida ? '+' : ''}{diff.toFixed(1)}%
                    </span>
                  </div>

                  {/* Metadatos */}
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-400 flex-wrap">
                    {alerta.orden_compra_id && (
                      <span className="flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        Orden: {alerta.orden_compra_id.slice(0, 8)}...
                      </span>
                    )}
                    {alerta.albaran_proveedor && (
                      <span>Albarán: {alerta.albaran_proveedor}</span>
                    )}
                    {alerta.fecha_recepcion && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(alerta.fecha_recepcion).toLocaleDateString('es-ES', {
                          day: '2-digit', month: 'short', year: 'numeric'
                        })}
                      </span>
                    )}
                  </div>

                  {/* Info de resolución */}
                  {!esPendiente && (
                    <div className={`mt-2 text-xs px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 ${
                      alerta.estado === 'aprobado' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {alerta.estado === 'aprobado' ? <Check className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                      {alerta.estado === 'aprobado' ? 'Aprobada' : 'Rechazada'}
                      {alerta.resuelto_por_nombre && ` por ${alerta.resuelto_por_nombre}`}
                      {alerta.fecha_resolucion && ` el ${new Date(alerta.fecha_resolucion).toLocaleDateString('es-ES')}`}
                      {alerta.precio_actualizado_en_catalogo && ' — Catálogo actualizado'}
                    </div>
                  )}
                  {!esPendiente && alerta.notas_resolucion && (
                    <p className="text-xs text-gray-500 mt-1 italic">Nota: {alerta.notas_resolucion}</p>
                  )}
                </div>

                {/* Acciones */}
                {esPendiente && puedeResolver && (
                  <div className="flex flex-col gap-2 lg:w-56 flex-shrink-0">
                    <textarea
                      value={notasResolucion[alerta.id] || ''}
                      onChange={(e) => setNotasResolucion(prev => ({...prev, [alerta.id]: e.target.value}))}
                      placeholder="Notas (opcional)"
                      rows={2}
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg resize-none focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                      data-testid={`notas-alerta-${alerta.id}`}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => resolverAlerta(alerta.id, 'aprobar')}
                        disabled={resolviendoId === alerta.id}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                        data-testid={`aprobar-alerta-${alerta.id}`}
                      >
                        <Check className="w-4 h-4" />
                        Aprobar
                      </button>
                      <button
                        onClick={() => resolverAlerta(alerta.id, 'rechazar')}
                        disabled={resolviendoId === alerta.id}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-white hover:bg-red-50 text-red-600 border border-red-300 text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                        data-testid={`rechazar-alerta-${alerta.id}`}
                      >
                        <XCircle className="w-4 h-4" />
                        Rechazar
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 text-center">
                      Al aprobar se actualiza el catálogo
                    </p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ComprasNecesidades = () => {
  // ?orden=<id>: enlace desde "Pedir a <proveedor>" de la lista de compra
  const location = useLocation();
  const ordenDestacada = new URLSearchParams(location.search).get('orden');
  const [activeTab, setActiveTab] = useState(ordenDestacada ? 'pedidos' : 'semana'); // semana, necesidades, pedidos, recepcion, alertas_precio
  // Pestañas en móvil: degradado a la derecha mientras quedan pestañas por ver
  const pestanasRef = useRef(null);
  const [masPestanas, setMasPestanas] = useState(false);
  const medirPestanas = () => {
    const el = pestanasRef.current;
    if (!el) return;
    setMasPestanas(el.scrollWidth - el.clientWidth - el.scrollLeft > 4);
  };
  useEffect(() => {
    medirPestanas();
    window.addEventListener('resize', medirPestanas);
    return () => window.removeEventListener('resize', medirPestanas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (ordenDestacada) setActiveTab('pedidos');
  }, [ordenDestacada]);
  const [alertasPrecioCount, setAlertasPrecioCount] = useState(0);
  const [necesidades, setNecesidades] = useState([]);
  const [necesidadesPorProveedor, setNecesidadesPorProveedor] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [eventosSeleccionados, setEventosSeleccionados] = useState(new Set()); // IDs de eventos seleccionados
  const [loading, setLoading] = useState(true);
  const [diasAdelante, setDiasAdelante] = useState(7);
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [vistaAgrupada, setVistaAgrupada] = useState(true);
  const [proveedoresExpandidos, setProveedoresExpandidos] = useState({});
  const [showModalEnvioEmail, setShowModalEnvioEmail] = useState(false);
  const [ordenParaEnviar, setOrdenParaEnviar] = useState(null);
  const [primeraCargeRealizada, setPrimeraCargaRealizada] = useState(false);

  // Efecto para la primera carga - obtener eventos y seleccionar todos
  // Clave única por evento. En multi-evento es el jornada_id (cada jornada es
  // su propia tarjeta seleccionable). En single-evento usamos el presupuesto_id.
  const eventoKey = (e) => e?.jornada_id || e?.presupuesto_id;
  // Convierte un Set de claves de eventos seleccionados en los query params del
  // backend: separa jornadas (jornadas_ids) y presupuestos enteros (presupuestos_ids).
  const construirQuerySeleccion = (eventosListado, seleccion) => {
    const jornadaIds = [];
    const presupuestoIds = [];
    eventosListado.forEach(e => {
      if (!seleccion.has(eventoKey(e))) return;
      if (e.jornada_id) jornadaIds.push(e.jornada_id);
      else presupuestoIds.push(e.presupuesto_id);
    });
    const params = new URLSearchParams();
    if (presupuestoIds.length > 0) params.set('presupuestos_ids', presupuestoIds.join(','));
    if (jornadaIds.length > 0) params.set('jornadas_ids', jornadaIds.join(','));
    return params.toString();
  };

  useEffect(() => {
    const cargarEventosIniciales = async () => {
      try {
        const urlTodosEventos = `${process.env.REACT_APP_BACKEND_URL}/api/necesidades-compra?dias_adelante=${diasAdelante}`;
        const responseTodos = await fetch(urlTodosEventos, {
          headers: { 'Authorization': `Bearer ${getAuthToken()}` }
        });
        const dataTodos = await responseTodos.json();
        const eventosCompletos = dataTodos.eventos_confirmados || [];
        setEventos(eventosCompletos);

        // Seleccionar todos los eventos en la primera carga
        if (eventosCompletos.length > 0) {
          setEventosSeleccionados(new Set(eventosCompletos.map(eventoKey)));
        }
        setPrimeraCargaRealizada(true);
      } catch (error) {
        console.error('Error cargando eventos iniciales:', error);
        setPrimeraCargaRealizada(true);
      }
    };
    
    // Cargar conteo de alertas de precio pendientes
    const cargarConteoAlertas = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/alertas-precio/pendientes/count`, {
          headers: { 'Authorization': `Bearer ${getAuthToken()}` }
        });
        if (res.ok) {
          const data = await res.json();
          setAlertasPrecioCount(data.count || 0);
        }
      } catch (e) { /* ignore */ }
    };

    cargarEventosIniciales();
    cargarConteoAlertas();
  }, []); // Solo se ejecuta una vez al montar

  // Efecto para recalcular necesidades cuando cambian los eventos seleccionados o días
  useEffect(() => {
    if (primeraCargeRealizada) {
      fetchNecesidades();
    }
  }, [diasAdelante, eventosSeleccionados, primeraCargeRealizada]);

  const fetchNecesidades = async () => {
    setLoading(true);
    try {
      // SIEMPRE obtener todos los eventos (sin filtro) para la lista completa
      const urlTodosEventos = `${process.env.REACT_APP_BACKEND_URL}/api/necesidades-compra?dias_adelante=${diasAdelante}`;
      const responseTodos = await fetch(urlTodosEventos, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });
      const dataTodos = await responseTodos.json();
      const eventosCompletos = dataTodos.eventos_confirmados || [];
      
      // Actualizar lista de eventos (siempre completa)
      setEventos(eventosCompletos);
      
      // Si no hay eventos seleccionados, limpiar necesidades
      if (eventosSeleccionados.size === 0) {
        setNecesidades([]);
        setNecesidadesPorProveedor([]);
        setProveedoresExpandidos({});
        setLoading(false);
        return;
      }
      
      // Construir URL con filtro de eventos seleccionados (presupuestos + jornadas)
      const seleccionParams = construirQuerySeleccion(eventosCompletos, eventosSeleccionados);
      let urlNecesidades = `${process.env.REACT_APP_BACKEND_URL}/api/necesidades-compra?dias_adelante=${diasAdelante}`;
      if (seleccionParams) urlNecesidades += `&${seleccionParams}`;
      
      const responseNecesidades = await fetch(urlNecesidades, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });
      const dataNecesidades = await responseNecesidades.json();
      
      // Actualizar necesidades (filtradas según selección)
      setNecesidades(dataNecesidades.necesidades || []);
      setNecesidadesPorProveedor(dataNecesidades.necesidades_por_proveedor || []);
      
      // Expandir por defecto proveedores con items por pedir
      const expandidos = {};
      dataNecesidades.necesidades_por_proveedor.forEach(p => {
        if (p.items_por_pedir > 0) {
          expandidos[p.proveedor_id] = true;
        }
      });
      setProveedoresExpandidos(expandidos);
    } catch (error) {
      console.error('Error fetching necesidades:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleProveedor = (proveedorId) => {
    setProveedoresExpandidos(prev => ({
      ...prev,
      [proveedorId]: !prev[proveedorId]
    }));
  };

  const toggleEvento = (eventoKey_) => {
    setEventosSeleccionados(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventoKey_)) newSet.delete(eventoKey_);
      else newSet.add(eventoKey_);
      return newSet;
    });
  };

  const seleccionarTodosEventos = () => {
    setEventosSeleccionados(new Set(eventos.map(eventoKey)));
  };

  const deseleccionarTodosEventos = () => {
    setEventosSeleccionados(new Set());
  };

  const descargarPDFListaCompra = async () => {
    try {
      const token = getAuthToken();
      const params = new URLSearchParams({ dias_adelante: String(diasAdelante) });
      if (eventosSeleccionados.size > 0) {
        // Distingue presupuestos completos (single-evento) y jornadas concretas
        const seleccionParams = construirQuerySeleccion(eventos, eventosSeleccionados);
        if (seleccionParams) {
          new URLSearchParams(seleccionParams).forEach((v, k) => params.set(k, v));
        }
      }
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/necesidades-compra/pdf?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert('Error generando PDF: ' + (err.detail || res.statusText));
        return;
      }
      const tipo = res.headers.get('Content-Type') || '';
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (tipo.includes('text/html')) {
        // Fallback: backend devolvió HTML (WeasyPrint no disponible). Abrir en pestaña para imprimir a PDF nativo del navegador.
        window.open(url, '_blank');
      } else {
        // PDF: descarga directa con nombre amigable
        const a = document.createElement('a');
        a.href = url;
        a.download = `lista-compra-${new Date().toISOString().slice(0, 10)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      console.error('Error descargando lista de compra:', e);
      alert('Error: ' + e.message);
    }
  };

  const handlePedirProveedor = async (proveedor) => {
    // Filtrar solo ingredientes que faltan
    const itemsAPedir = proveedor.ingredientes.filter(i => i.falta > 0);
    
    if (itemsAPedir.length === 0) {
      alert('No hay ingredientes por pedir de este proveedor');
      return;
    }

    const confirmacion = confirm(
      `¿Crear pedido a ${proveedor.proveedor_nombre}?\n\n` +
      `Items a pedir: ${itemsAPedir.length}\n` +
      `Total estimado: Se calculará según precios`
    );

    if (!confirmacion) return;

    try {
      // Preparar items para el pedido
      const items = itemsAPedir.map(item => ({
        ingrediente_id: item.ingrediente_id,
        cantidad_pedida: item.falta,
        unidad: item.unidad,
        precio_estimado: item.precio_unitario || 0  // Usar precio actual del ingrediente
      }));

      const payload = {
        proveedor_id: proveedor.proveedor_id,
        fecha_entrega_esperada: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // +7 días
        items: items,
        observaciones: `Pedido automático generado desde necesidades`
      };

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ordenes-compra`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        const result = await response.json();
        alert(`✅ Pedido creado: ${result.total_items} items para ${result.proveedor}`);
        // Cambiar a tab de pedidos
        setActiveTab('pedidos');
        // Refrescar necesidades
        fetchNecesidades();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error creando pedido:', error);
      alert('Error al crear el pedido');
    }
  };

  const [datosEnvio, setDatosEnvio] = useState({
    emailProveedor: '',
    fechaEntrega: '',
    observaciones: '',
    mensajeEmail: ''
  });

  const abrirModalEnvioEmail = async (orden) => {
    // Obtener datos del proveedor
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/proveedores`,
        {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`
          }
        }
      );
      
      if (response.ok) {
        const proveedores = await response.json();
        const proveedor = proveedores.find(p => p.id === orden.proveedor_id);
        
        setDatosEnvio({
          emailProveedor: proveedor?.email || '',
          fechaEntrega: orden.fecha_entrega_esperada || '',
          observaciones: orden.observaciones || '',
          mensajeEmail: orden.pedido_por_dia
            ? `Estimado/a proveedor,\n\nAdjuntamos nueva orden de compra con entregas los días ${(orden.fechas_entrega || []).map(ddmm).join(', ')} (el detalle de cada día va en el pedido).\n\nGracias,\nDuetto Catering`
            : `Estimado/a proveedor,\n\nAdjuntamos nueva orden de compra para entrega el ${orden.fecha_entrega_esperada || '[fecha]'}.\n\nGracias,\nDuetto Catering`
        });
      }
    } catch (error) {
      console.error('Error obteniendo proveedor:', error);
    }
    
    setOrdenParaEnviar(orden);
    setShowModalEnvioEmail(true);
  };

  const enviarEmailOrden = async () => {
    if (!ordenParaEnviar) return;

    // Validar email
    if (!datosEnvio.emailProveedor || !datosEnvio.emailProveedor.includes('@')) {
      alert('Por favor, introduce un email válido del proveedor');
      return;
    }

    try {
      setLoading(true);
      
      // Primero actualizar los datos de la orden si han cambiado
      if (datosEnvio.fechaEntrega !== ordenParaEnviar.fecha_entrega_esperada || 
          datosEnvio.observaciones !== ordenParaEnviar.observaciones) {
        
        const updateResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/ordenes-compra/${ordenParaEnviar.id}`,
          {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${getAuthToken()}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              proveedor_id: ordenParaEnviar.proveedor_id,
              fecha_entrega_esperada: datosEnvio.fechaEntrega,
              observaciones: datosEnvio.observaciones,
              items: ordenParaEnviar.items.map(item => ({
                ingrediente_id: item.ingrediente_id,
                cantidad_pedida: item.cantidad_pedida,
                unidad: item.unidad,
                precio_estimado: item.precio_estimado
              }))
            })
          }
        );
        
        if (!updateResponse.ok) {
          alert('Error al actualizar la orden');
          setLoading(false);
          return;
        }
      }
      
      // Ahora enviar el email con el email actualizado del proveedor y mensaje personalizado
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ordenes-compra/${ordenParaEnviar.id}/enviar-email`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email_proveedor: datosEnvio.emailProveedor,
            mensaje_email: datosEnvio.mensajeEmail
          })
        }
      );

      if (response.ok) {
        const result = await response.json();
        alert(`✅ ${result.message}`);
        setShowModalEnvioEmail(false);
        setOrdenParaEnviar(null);
        // Refrescar la lista de órdenes si estamos en la pestaña de pedidos
        if (activeTab === 'pedidos') {
          // Trigger refresh of orders list
          window.location.reload(); // Simple refresh for now
        }
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error enviando email:', error);
      alert('Error al enviar el email');
    } finally {
      setLoading(false);
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'pedir':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'bajo':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'ok':
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getEstadoIcon = (estado) => {
    switch (estado) {
      case 'pedir':
        return <AlertCircle className="w-4 h-4" />;
      case 'bajo':
        return <AlertTriangle className="w-4 h-4" />;
      case 'ok':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getEstadoTexto = (estado) => {
    switch (estado) {
      case 'pedir':
        return 'PEDIR';
      case 'bajo':
        return 'Stock Bajo';
      case 'ok':
        return 'OK';
      default:
        return estado;
    }
  };

  const necesidadesFiltradas = necesidades.filter(n => 
    filtroEstado === 'todos' || n.estado === filtroEstado
  );

  const stats = {
    total: necesidades.length,
    pedir: necesidades.filter(n => n.estado === 'pedir').length,
    bajo: necesidades.filter(n => n.estado === 'bajo').length,
    ok: necesidades.filter(n => n.estado === 'ok').length
  };

  // El cálculo de necesidades (antiguo) solo bloquea su pestaña
  if (loading && activeTab === 'necesidades') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-duetto-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Calculando necesidades de compra...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header - Responsive */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Compras y Almacén</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Gestión completa de compras, pedidos y recepción de mercancía
          </p>
        </div>
        <div className="flex gap-2">
          <select
            value={diasAdelante}
            onChange={(e) => {
              setDiasAdelante(parseInt(e.target.value));
              setEventosSeleccionados(new Set()); // Limpiar selección al cambiar días
            }}
            className="w-full sm:w-auto px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent text-sm sm:text-base"
          >
            <option value={7}>7 días</option>
            <option value={15}>15 días</option>
            <option value={30}>30 días</option>
            <option value={60}>60 días</option>
          </select>
        </div>
      </div>

      {/* Tabs - Responsive (en móvil, degradado a la derecha mientras quedan pestañas) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="relative">
        <div ref={pestanasRef} onScroll={medirPestanas} className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex -mb-px min-w-max">
            <button
              onClick={() => setActiveTab('semana')}
              className={`px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'semana'
                  ? 'border-duetto-primary text-duetto-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="tab-compra-semana"
              data-tour="compras-tab-semana"
            >
              <div className="flex items-center gap-1 sm:gap-2">
                <Calendar className="w-4 h-4" />
                <span className="hidden sm:inline">Compra de la semana</span>
                <span className="sm:hidden">Semana</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('necesidades')}
              className={`px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'necesidades'
                  ? 'border-duetto-primary text-duetto-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-1 sm:gap-2">
                <Package className="w-4 h-4" />
                <span className="hidden sm:inline">Necesidades</span>
                <span className="sm:hidden">Nec.</span>
                {stats.pedir > 0 && (
                  <span className="bg-red-100 text-red-800 text-xs px-1.5 sm:px-2 py-0.5 rounded-full">
                    {stats.pedir}
                  </span>
                )}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('pedidos')}
              className={`px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'pedidos'
                  ? 'border-duetto-primary text-duetto-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-1 sm:gap-2">
                <ShoppingCart className="w-4 h-4" />
                <span className="hidden sm:inline">Hacer Pedido</span>
                <span className="sm:hidden">Pedidos</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('recepcion')}
              className={`px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'recepcion'
                  ? 'border-duetto-primary text-duetto-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-1 sm:gap-2">
                <TrendingUp className="w-4 h-4" />
                <span className="hidden sm:inline">Recibir Mercancía</span>
                <span className="sm:hidden">Recibir</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('alertas_precio')}
              className={`px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'alertas_precio'
                  ? 'border-duetto-primary text-duetto-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="tab-alertas-precio-btn"
            >
              <div className="flex items-center gap-1 sm:gap-2">
                <DollarSign className="w-4 h-4" />
                <span className="hidden sm:inline">Alertas Precio</span>
                <span className="sm:hidden">Alertas</span>
                {alertasPrecioCount > 0 && (
                  <span className="bg-red-100 text-red-800 text-xs px-1.5 sm:px-2 py-0.5 rounded-full font-semibold">
                    {alertasPrecioCount}
                  </span>
                )}
              </div>
            </button>
          </nav>
        </div>
        {masPestanas && (
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-white to-transparent sm:hidden rounded-tr-xl" aria-hidden="true" />
        )}
        </div>

        <div className="p-3 sm:p-6">
          {/* TAB: COMPRA DE LA SEMANA (lista de compra v2 de varios eventos) */}
          {activeTab === 'semana' && <CompraSemana />}

          {/* TAB: NECESIDADES */}
          {activeTab === 'necesidades' && (
            <div className="space-y-4 sm:space-y-6">
              {/* Stats Cards - Responsive */}
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm font-medium text-gray-600">Total</p>
                      <p className="text-xl sm:text-3xl font-bold text-gray-900 mt-1">{stats.total}</p>
                    </div>
                    <div className="w-8 h-8 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Package className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-red-200 p-3 sm:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm font-medium text-gray-600">Por Pedir</p>
                      <p className="text-xl sm:text-3xl font-bold text-red-600 mt-1">{stats.pedir}</p>
                    </div>
                    <div className="w-8 h-8 sm:w-12 sm:h-12 bg-red-100 rounded-lg flex items-center justify-center">
                      <AlertCircle className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-yellow-200 p-3 sm:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm font-medium text-gray-600">Stock Bajo</p>
                      <p className="text-xl sm:text-3xl font-bold text-yellow-600 mt-1">{stats.bajo}</p>
                    </div>
                    <div className="w-8 h-8 sm:w-12 sm:h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                      <AlertTriangle className="w-4 h-4 sm:w-6 sm:h-6 text-yellow-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-green-200 p-3 sm:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm font-medium text-gray-600">Stock OK</p>
                      <p className="text-xl sm:text-3xl font-bold text-green-600 mt-1">{stats.ok}</p>
                    </div>
                    <div className="w-8 h-8 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 sm:w-6 sm:h-6 text-green-600" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Eventos Confirmados */}
              <div className="bg-gray-50 rounded-xl border border-gray-200 p-3 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <Calendar className="w-5 h-5 text-duetto-primary" />
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                      Eventos Confirmados - Próximos {diasAdelante} días
                    </h2>
                    <span className="bg-duetto-primary text-white text-xs px-2 py-1 rounded-full">
                      {eventosSeleccionados.size} de {eventos.length} seleccionados
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={descargarPDFListaCompra}
                      disabled={eventos.length === 0}
                      title={eventosSeleccionados.size === 0 ? 'Descargará la lista de TODOS los eventos del periodo' : `Descargará la lista de ${eventosSeleccionados.size} evento(s) seleccionado(s)`}
                      className="px-3 py-2.5 sm:py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Download className="w-4 h-4" />
                      Descargar PDF
                    </button>
                    <button
                      onClick={seleccionarTodosEventos}
                      className="px-3 py-2.5 sm:py-1 text-sm bg-duetto-primary text-white rounded-lg hover:bg-duetto-secondary transition-colors"
                    >
                      Seleccionar Todos
                    </button>
                    <button
                      onClick={deseleccionarTodosEventos}
                      className="px-3 py-2.5 sm:py-1 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Deseleccionar Todos
                    </button>
                  </div>
                </div>
                {eventos.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No hay eventos confirmados en este período</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {eventos.map((evento) => {
                      const key = eventoKey(evento);
                      const isSelected = eventosSeleccionados.has(key);
                      const esMulti = !!evento.es_jornada_multi;
                      // Multi-evento: fondo y borde purple para distinguir en
                      // el selector (mismo esquema que el calendario).
                      const bg = esMulti
                        ? (isSelected ? 'bg-purple-100 border-purple-600 border-[3px]' : 'bg-purple-50 border-purple-300 hover:border-purple-500')
                        : (isSelected ? 'bg-white border-green-500 border-[3px]' : 'bg-white border-gray-300 hover:border-gray-400');
                      const fechaBlock = esMulti
                        ? (isSelected ? 'bg-purple-600 text-white' : 'bg-purple-200 text-purple-800')
                        : (isSelected ? 'bg-duetto-primary text-white' : 'bg-gray-200 text-gray-700');
                      return (
                        <div
                          key={key}
                          onClick={() => toggleEvento(key)}
                          className={`flex items-center justify-between gap-2 p-3 rounded-lg shadow-sm border-2 cursor-pointer transition-all hover:shadow-md overflow-hidden ${bg}`}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className={`shrink-0 text-center rounded-lg p-2 min-w-[50px] ${fechaBlock}`}>
                              <div className="text-xl font-bold">
                                {new Date(evento.fecha_evento).getDate()}
                              </div>
                              <div className="text-xs">
                                {new Date(evento.fecha_evento).toLocaleDateString('es-ES', { month: 'short' })}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              {esMulti && (
                                <div className="mb-0.5">
                                  <span className="inline-block bg-purple-200 text-purple-800 text-[10px] font-semibold px-1.5 py-0.5 rounded">
                                    Jornada {evento.numero_jornada}/{evento.total_jornadas_multi}
                                  </span>
                                </div>
                              )}
                              <p className={`font-semibold text-sm truncate ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                                {evento.cliente_nombre}
                              </p>
                              <p className="text-xs text-gray-500 truncate">{evento.lugar_evento || 'Sin ubicación'}</p>
                            </div>
                          </div>
                          <div className="shrink-0 flex items-center gap-1 text-sm">
                            <Users className="w-4 h-4 text-gray-400" />
                            <span className={`font-medium ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                              {evento.num_personas}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Toggle Vista */}
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Ingredientes Necesarios</h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => setVistaAgrupada(true)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      vistaAgrupada ? 'bg-duetto-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Por Proveedor
                  </button>
                  <button
                    onClick={() => setVistaAgrupada(false)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      !vistaAgrupada ? 'bg-duetto-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Lista Completa
                  </button>
                </div>
              </div>

              {/* Vista Agrupada por Proveedor */}
              {vistaAgrupada ? (
                <div className="space-y-4">
                  {necesidadesPorProveedor.map((proveedor) => (
                    <div key={proveedor.proveedor_id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                      {/* Header del Proveedor */}
                      <div 
                        className="bg-gradient-to-r from-duetto-primary to-duetto-secondary text-white p-4 cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => toggleProveedor(proveedor.proveedor_id)}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                              <Truck className="w-5 h-5" />
                            </div>
                            <div>
                              <h3 className="text-lg font-bold">{proveedor.proveedor_nombre}</h3>
                              <p className="text-sm text-white/80">
                                {proveedor.total_items} ingrediente{proveedor.total_items !== 1 ? 's' : ''}
                                {proveedor.items_por_pedir > 0 && (
                                  <span className="ml-2 bg-red-500 px-2 py-0.5 rounded-full text-xs font-medium">
                                    {proveedor.items_por_pedir} por pedir
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 w-full sm:w-auto">
                            {proveedor.items_por_pedir > 0 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePedirProveedor(proveedor);
                                }}
                                className="bg-white text-duetto-primary px-4 py-2.5 sm:py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors flex items-center justify-center gap-2 w-full sm:w-auto"
                              >
                                <ShoppingCart className="w-4 h-4" />
                                Pedir Todo
                              </button>
                            )}
                            {proveedoresExpandidos[proveedor.proveedor_id] ? (
                              <ChevronDown className="w-5 h-5" />
                            ) : (
                              <ChevronRight className="w-5 h-5" />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Tabla de Ingredientes */}
                      {proveedoresExpandidos[proveedor.proveedor_id] && (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 bg-gray-50 z-[1]">Ingrediente</th>
                                <th className="px-3 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Necesario</th>
                                <th className="px-3 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Envases</th>
                                <th className="px-3 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Stock</th>
                                <th className="px-3 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Pedido</th>
                                <th className="px-3 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Falta</th>
                                <th className="px-3 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {proveedor.ingredientes.map((item) => (
                                <tr key={item.ingrediente_id} className="hover:bg-gray-50">
                                  <td className="px-3 sm:px-6 py-4 sticky left-0 bg-white z-[1]">
                                    <div className="font-medium text-gray-900">{item.nombre}</div>
                                  </td>
                                  <td className="px-3 sm:px-6 py-4 text-center text-gray-900 font-medium">
                                    {item.cantidad_necesaria} {item.unidad}
                                  </td>
                                  <td className="px-3 sm:px-6 py-4 text-center">
                                    {(() => {
                                      const upe = item.unidades_por_paquete || 1;
                                      const envases = Math.ceil((item.cantidad_necesaria || 0) / upe);
                                      return (
                                        <div>
                                          <span className="font-medium text-gray-900">{envases}</span>
                                          <div className="text-[10px] text-gray-400">{upe} {item.unidad}/env</div>
                                        </div>
                                      );
                                    })()}
                                  </td>
                                  <td className="px-3 sm:px-6 py-4 text-center">
                                    <div className="flex flex-col items-center gap-1">
                                      <span className={`font-medium ${item.stock_actual <= 0 ? 'text-red-600' : 'text-gray-900'}`}>
                                        {item.stock_actual} {item.unidad}
                                      </span>
                                      {item.cantidad_caducada > 0 && (
                                        <span className="text-xs text-orange-600 flex items-center gap-1" title={`${item.cantidad_caducada} ${item.unidad} en ${item.lotes_caducados} lote(s) caducado(s)`}>
                                          <AlertTriangle className="w-3 h-3" />
                                          {item.cantidad_caducada} caduc.
                                        </span>
                                      )}
                                      {item.stock_reservado > 0 && (
                                        <span className="text-xs text-amber-600 flex items-center gap-1" title="Stock reservado para otros eventos">
                                          <AlertCircle className="w-3 h-3" />
                                          {item.stock_reservado} reserv.
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-3 sm:px-6 py-4 text-center">
                                    {item.cantidad_pedida > 0 ? (
                                      <span className="text-blue-600 font-medium">
                                        {item.cantidad_pedida} {item.unidad}
                                      </span>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </td>
                                  <td className="px-3 sm:px-6 py-4 text-center">
                                    <span className={`font-bold ${item.falta > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                      {item.falta > 0 ? `${item.falta} ${item.unidad}` : '-'}
                                    </span>
                                  </td>
                                  <td className="px-3 sm:px-6 py-4 text-center">
                                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border ${getEstadoColor(item.estado)}`}>
                                      {getEstadoIcon(item.estado)}
                                      {getEstadoTexto(item.estado)}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                /* Vista Lista Completa */
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <div className="p-3 sm:p-6 border-b border-gray-200">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setFiltroEstado('todos')}
                        className={`px-3 py-2 sm:py-1 rounded-lg text-sm font-medium transition-colors ${
                          filtroEstado === 'todos' ? 'bg-duetto-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Todos ({stats.total})
                      </button>
                      <button
                        onClick={() => setFiltroEstado('pedir')}
                        className={`px-3 py-2 sm:py-1 rounded-lg text-sm font-medium transition-colors ${
                          filtroEstado === 'pedir' ? 'bg-red-600 text-white' : 'bg-red-100 text-red-700 hover:bg-red-200'
                        }`}
                      >
                        Por Pedir ({stats.pedir})
                      </button>
                      <button
                        onClick={() => setFiltroEstado('bajo')}
                        className={`px-3 py-2 sm:py-1 rounded-lg text-sm font-medium transition-colors ${
                          filtroEstado === 'bajo' ? 'bg-yellow-600 text-white' : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                        }`}
                      >
                        Stock Bajo ({stats.bajo})
                      </button>
                      <button
                        onClick={() => setFiltroEstado('ok')}
                        className={`px-3 py-2 sm:py-1 rounded-lg text-sm font-medium transition-colors ${
                          filtroEstado === 'ok' ? 'bg-green-600 text-white' : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        OK ({stats.ok})
                      </button>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 bg-gray-50 z-[1]">Ingrediente</th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proveedor</th>
                          <th className="px-3 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Necesario</th>
                          <th className="px-3 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Envases</th>
                          <th className="px-3 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Stock</th>
                          <th className="px-3 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Pedido</th>
                          <th className="px-3 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Falta</th>
                          <th className="px-3 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {necesidadesFiltradas.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="px-3 sm:px-6 py-12 text-center text-gray-500">
                              No hay ingredientes que cumplan el filtro seleccionado
                            </td>
                          </tr>
                        ) : (
                          necesidadesFiltradas.map((item) => (
                            <tr key={item.ingrediente_id} className="hover:bg-gray-50">
                              <td className="px-3 sm:px-6 py-4 sticky left-0 bg-white z-[1]">
                                <div className="font-medium text-gray-900">{item.nombre}</div>
                              </td>
                              <td className="px-3 sm:px-6 py-4 text-gray-600 text-sm">
                                {item.proveedor_nombre}
                              </td>
                              <td className="px-3 sm:px-6 py-4 text-center text-gray-900 font-medium">
                                {item.cantidad_necesaria} {item.unidad}
                              </td>
                              <td className="px-3 sm:px-6 py-4 text-center">
                                {(() => {
                                  const upe = item.unidades_por_paquete || 1;
                                  const envases = Math.ceil((item.cantidad_necesaria || 0) / upe);
                                  return (
                                    <div data-testid={`envases-lista-${item.ingrediente_id}`}>
                                      <span className="font-medium text-gray-900">{envases}</span>
                                      <div className="text-[10px] text-gray-400">{upe} {item.unidad}/env</div>
                                    </div>
                                  );
                                })()}
                              </td>
                              <td className="px-3 sm:px-6 py-4 text-center">
                                <div className="flex flex-col items-center gap-1">
                                  <span className={`font-medium ${item.stock_actual <= 0 ? 'text-red-600' : 'text-gray-900'}`}>
                                    {item.stock_actual} {item.unidad}
                                  </span>
                                  {item.cantidad_caducada > 0 && (
                                    <span className="text-xs text-orange-600 flex items-center gap-1" title={`${item.cantidad_caducada} ${item.unidad} en ${item.lotes_caducados} lote(s) caducado(s)`}>
                                      <AlertTriangle className="w-3 h-3" />
                                      {item.cantidad_caducada} caduc.
                                    </span>
                                  )}
                                  {item.stock_reservado > 0 && (
                                    <span className="text-xs text-amber-600 flex items-center gap-1" title="Stock reservado para otros eventos">
                                      <AlertCircle className="w-3 h-3" />
                                      {item.stock_reservado} reserv.
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 sm:px-6 py-4 text-center">
                                {item.cantidad_pedida > 0 ? (
                                  <span className="text-blue-600 font-medium">
                                    {item.cantidad_pedida} {item.unidad}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-3 sm:px-6 py-4 text-center">
                                <span className={`font-bold ${item.falta > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                  {item.falta > 0 ? `${item.falta} ${item.unidad}` : '-'}
                                </span>
                              </td>
                              <td className="px-3 sm:px-6 py-4 text-center">
                                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border ${getEstadoColor(item.estado)}`}>
                                  {getEstadoIcon(item.estado)}
                                  {getEstadoTexto(item.estado)}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB: HACER PEDIDO */}
          {activeTab === 'pedidos' && <TabHacerPedido abrirModalEnvioEmail={abrirModalEnvioEmail} ordenDestacada={ordenDestacada} />}

          {/* TAB: RECIBIR MERCANCÍA */}
          {activeTab === 'recepcion' && <TabRecibirMercancia />}

          {/* TAB: ALERTAS DE PRECIO */}
          {activeTab === 'alertas_precio' && (
            <TabAlertasPrecio onCountUpdate={setAlertasPrecioCount} />
          )}
        </div>
      </div>

      {/* Modal de Confirmación de Envío de Email */}
      {showModalEnvioEmail && ordenParaEnviar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90dvh] flex flex-col">
            {/* Header del Modal */}
            <div className="bg-gradient-to-r from-green-600 to-green-500 text-white p-4 sm:p-6 rounded-t-2xl flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <Package className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">Enviar Pedido</h2>
                    <p className="text-white/80 text-sm">#{ordenParaEnviar.id.substring(0, 8).toUpperCase()}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowModalEnvioEmail(false);
                    setOrdenParaEnviar(null);
                  }}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Contenido del Modal - Scrollable */}
            <div className="p-4 sm:p-6 overflow-y-auto flex-1 min-h-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Columna Izquierda: Datos de la Orden */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Datos del Pedido</h3>
                  
                  {/* Proveedor */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      🏢 Proveedor
                    </label>
                    <input
                      type="text"
                      value={ordenParaEnviar.proveedor_nombre}
                      disabled
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 font-semibold"
                    />
                  </div>

                  {/* Email Proveedor - Editable */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      📧 Email del Proveedor
                    </label>
                    <input
                      type="email"
                      value={datosEnvio.emailProveedor}
                      onChange={(e) => setDatosEnvio({...datosEnvio, emailProveedor: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                      placeholder="proveedor@email.com"
                    />
                  </div>

                  {/* Fecha Entrega - Editable */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      📅 Fecha Entrega Esperada
                    </label>
                    <input
                      type="date"
                      value={datosEnvio.fechaEntrega}
                      onChange={(e) => setDatosEnvio({...datosEnvio, fechaEntrega: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                    />
                  </div>

                  {/* Total */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      💰 Total Estimado
                    </label>
                    <div className="text-2xl font-bold text-green-600">
                      €{ordenParaEnviar.total_estimado.toFixed(2)}
                    </div>
                  </div>
                  
                  {/* Info del asunto */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-700">
                      <strong>Asunto del email:</strong><br/>
                      <span className="font-mono">Pedido Duetto Catering #{ordenParaEnviar.id.substring(0, 8).toUpperCase()}</span>
                    </p>
                  </div>
                </div>

                {/* Columna Derecha: Mensajes */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Mensaje y Observaciones</h3>
                  
                  {/* Mensaje del Email - Editable */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      💬 Mensaje del Email
                    </label>
                    <textarea
                      value={datosEnvio.mensajeEmail}
                      onChange={(e) => setDatosEnvio({...datosEnvio, mensajeEmail: e.target.value})}
                      rows={8}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent font-mono text-sm"
                      placeholder="Escribe el mensaje que se enviará al proveedor..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Este mensaje aparecerá en el cuerpo del email junto con el detalle de productos
                    </p>
                  </div>

                  {/* Observaciones - Editable */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      📝 Observaciones (aparecen en PDF)
                    </label>
                    <textarea
                      value={datosEnvio.observaciones}
                      onChange={(e) => setDatosEnvio({...datosEnvio, observaciones: e.target.value})}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                      placeholder="Notas que aparecerán en el PDF..."
                    />
                  </div>
                </div>
              </div>

              {/* Historial de Comunicaciones */}
              {ordenParaEnviar.envio_email && (
                <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Historial de Comunicaciones
                  </h3>
                  <div className="space-y-2">
                    <div className="bg-white p-3 rounded border border-blue-100">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-xs font-medium text-gray-900">
                            📤 Enviado el {ordenParaEnviar.envio_email.fecha_envio} a las {ordenParaEnviar.envio_email.hora_envio}
                          </p>
                          <p className="text-xs text-gray-600 mt-1">
                            <strong>Para:</strong> {ordenParaEnviar.envio_email.destinatario} ({ordenParaEnviar.envio_email.destinatario_nombre})
                          </p>
                          <p className="text-xs text-gray-600">
                            <strong>Asunto:</strong> {ordenParaEnviar.envio_email.asunto}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Enviado por: {ordenParaEnviar.envio_email.enviado_por}
                          </p>
                        </div>
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                          Enviado
                        </span>
                      </div>
                    </div>
                    
                    {ordenParaEnviar.respuestas_proveedor && ordenParaEnviar.respuestas_proveedor.length > 0 ? (
                      <>
                        {/* Alerta si hay respuestas sin leer */}
                        {ordenParaEnviar.tiene_respuestas_sin_leer && (
                          <div className="bg-red-50 border-l-4 border-red-400 p-3 mb-2">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start">
                                <div className="flex-shrink-0">
                                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                </div>
                                <div className="ml-3">
                                  <p className="text-sm font-medium text-red-800">
                                    ⚠️ El proveedor ha respondido - Requiere atención
                                  </p>
                                  <p className="text-xs text-red-700 mt-1">
                                    Revisa el mensaje y toma las acciones necesarias
                                  </p>
                                </div>
                              </div>
                              <button
                                onClick={async () => {
                                  try {
                                    const response = await fetch(
                                      `${process.env.REACT_APP_BACKEND_URL}/api/ordenes-compra/${ordenParaEnviar.id}/marcar-respuestas-leidas`,
                                      {
                                        method: 'POST',
                                        headers: {
                                          'Authorization': `Bearer ${getAuthToken()}`
                                        }
                                      }
                                    );
                                    if (response.ok) {
                                      // Actualizar el estado local
                                      setOrdenParaEnviar({
                                        ...ordenParaEnviar,
                                        tiene_respuestas_sin_leer: false
                                      });
                                      // Refrescar la lista de órdenes
                                      fetchOrdenes();
                                    }
                                  } catch (error) {
                                    console.error('Error marcando como leído:', error);
                                  }
                                }}
                                className="text-xs px-3 py-1 bg-red-100 hover:bg-red-200 text-red-800 rounded transition-colors font-medium"
                              >
                                Marcar como leído
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {ordenParaEnviar.respuestas_proveedor.map((respuesta, idx) => (
                          <div key={idx} className={`p-3 rounded border ${ordenParaEnviar.tiene_respuestas_sin_leer ? 'bg-yellow-50 border-yellow-200' : 'bg-white border-green-100'}`}>
                            <p className="text-xs font-medium text-gray-900">
                              📥 Respuesta recibida el {respuesta.fecha}
                            </p>
                            <p className="text-xs text-gray-600 mt-1">
                              <strong>De:</strong> {respuesta.from}
                            </p>
                            <p className="text-xs text-gray-600">
                              <strong>Asunto:</strong> {respuesta.subject}
                            </p>
                            {respuesta.mensaje && (
                              <div className="mt-2 p-2 bg-white border border-gray-200 rounded">
                                <p className="text-xs font-medium text-gray-700 mb-1">💬 Mensaje del proveedor:</p>
                                <p className="text-xs text-gray-800 whitespace-pre-wrap">
                                  {respuesta.mensaje}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </>
                    ) : (
                      <p className="text-xs text-gray-500 italic">
                        Esperando respuesta del proveedor...
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Advertencia */}
              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">Una vez enviado:</p>
                    <ul className="text-xs text-yellow-700 mt-1 space-y-1">
                      <li>• No podrás editar el pedido</li>
                      <li>• El proveedor recibirá un email con el detalle de productos y PDF adjunto</li>
                      <li>• Se registrará la fecha, hora y destinatario del envío</li>
                      <li>• Las respuestas del proveedor se recibirán en pedidos@duettocatering.com</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer del Modal - Siempre Visible */}
            <div className="border-t border-gray-200 p-4 bg-gray-50 flex gap-3">
              <button
                onClick={() => {
                  setShowModalEnvioEmail(false);
                  setOrdenParaEnviar(null);
                }}
                className="flex-1 px-5 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={enviarEmailOrden}
                disabled={loading}
                className="flex-1 px-5 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Enviando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Confirmar y Enviar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComprasNecesidades;
